// Quote Dashboard — Purchase Order modal helpers (extracted from
// sections/rep-quote-dashboard.liquid to keep that section under Shopify's
// 256 KB template limit).
//
// Exposes window.dashboardPoLens with:
//   .attach(wrap)           — binds the floating "Fullscreen" button
//   .renderVendorRow(...)   — builds one Purchase Orders row per vendor
//
// `wrap` is the .rqd-po-modal__pdf-wrap element. The PDF iframe stays fully
// interactive (scroll/zoom work inside the browser's PDF viewer). A small
// floating button in the corner is the only thing that opens fullscreen.
(function () {
  /* CUSTOM_SENDWINDOW_BY_CLAUDE — "Fri 6:00 AM" for a scheduled PO's row + tooltip. Rendered in
     the BROWSER's zone rather than forced to MT: the label answers "when does this leave?" for
     the person reading it, and every rep on this dashboard is on MT anyway. */
  function _fmtSched(iso) {
    try {
      return new Date(iso).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } catch (_) { return ''; }
  }
  function attach(wrap) {
    if (!wrap) return;
    var btn = wrap.querySelector('.rqd-po-modal__pdf-fullscreen-btn');
    var iframe = wrap.querySelector('.rqd-po-modal__pdf-iframe');
    if (!btn || !iframe) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var src = iframe.getAttribute('src');
      if (!src) return;
      openFullscreen(src);
    });
  }

  // Singleton fullscreen overlay — created on demand so the section markup
  // stays small. Esc, X button, and backdrop click all dismiss.
  var overlayEl = null;
  function ensureOverlay() {
    if (overlayEl) return overlayEl;
    overlayEl = document.createElement('div');
    overlayEl.className = 'rqd-po-fullscreen rqd-po-fullscreen--hidden';
    overlayEl.innerHTML =
      '<div class="rqd-po-fullscreen__backdrop"></div>' +
      '<div class="rqd-po-fullscreen__panel">' +
        '<button type="button" class="rqd-po-fullscreen__close" aria-label="Close">&times;</button>' +
        '<iframe class="rqd-po-fullscreen__iframe" title="PO PDF"></iframe>' +
      '</div>';
    document.body.appendChild(overlayEl);
    overlayEl.querySelector('.rqd-po-fullscreen__close').addEventListener('click', closeFullscreen);
    overlayEl.querySelector('.rqd-po-fullscreen__backdrop').addEventListener('click', closeFullscreen);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlayEl && !overlayEl.classList.contains('rqd-po-fullscreen--hidden')) {
        closeFullscreen();
      }
    });
    return overlayEl;
  }
  function openFullscreen(src) {
    var ov = ensureOverlay();
    var iframe = ov.querySelector('iframe');
    iframe.src = src;
    ov.classList.remove('rqd-po-fullscreen--hidden');
    document.body.style.overflow = 'hidden';
  }
  function closeFullscreen() {
    if (!overlayEl) return;
    overlayEl.classList.add('rqd-po-fullscreen--hidden');
    var iframe = overlayEl.querySelector('iframe');
    if (iframe) iframe.src = 'about:blank';  // free the PDF resource
    document.body.style.overflow = '';
  }

  // Renders one Purchasing row per vendor — laid out like the AR/AP "Invoiced"
  // row (.rqd-toggle + checkbox + label + date + right-aligned action buttons)
  // so the Purchasing card matches the Shipments / AR-AP visual vocabulary.
  // Voided POs render as slim locked audit-trail rows below the active row.
  //
  // For online Flow B orders, an additional "freight quote" handshake row
  // renders ABOVE the PO row. Three states drive its content:
  //   1. No `requestedAt` → "Request Freight Quote" button
  //   2. Has `requestedAt`, no `cost` → "Sent {date}" + $___ + Save
  //   3. Has `cost` → "Freight Cost: $X — saved {date}" (Generate PO unlocks)
  // Generate PO stays disabled in states 1+2 so the rep can't accidentally
  // PO ECORE before the freight cost is locked in.
  function renderVendorRow(q, vendor, pos, helpers) {
    var esc = helpers.esc, formatDate = helpers.formatDate, money = helpers.money;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — ⛔ `esc` DOES NOT ESCAPE DOUBLE QUOTES. It is
       textContent -> innerHTML, which handles & < > and leaves `"` alone — correct for element
       TEXT, unsafe the moment the result is dropped inside title="…".

       This was already live and already broken on exactly the row this session is about: the
       ambiguous-alias gate reason is built as '"' + vendor + '" is a quote name used by …', so the
       Generate PO button rendered title=""Blue Sky Fitness Supply" is …' — the browser reads the
       title as empty and treats the rest as stray attributes, which is why that tooltip has never
       said anything. Every other call site here interpolates a vendor name or a PO number, which
       is why nothing noticed.

       It matters more now: a matched PRODUCT TITLE goes into an attribute below, and 48" turf and
       24" plates are ordinary names in this catalogue. */
    function escAttr(str) { return esc(str).replace(/"/g, '&quot;'); }
    var safeVendor = esc(vendor);
    var safeRef = esc(q.reference);
    var active = pos.filter(function (p) { return p.status !== 'voided'; });
    var voided = pos.filter(function (p) { return p.status === 'voided'; });
    var html = '';

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — Quote Alias. This row's name came off the
       Shopify line item, and for white-labelled goods that name is OURS ("Blue Sky Fitness
       Supply"), not the supplier's. A supplier claims that name with a Quote Alias on its PO
       Profile.

         one claimant  -> resolved silently, the row just says who it goes to
         two or more   -> the row cannot know, so it ASKS, and nothing that emails a supplier
                          may fire until it has an answer

       The row keeps its own name either way: it is the name on the quote, and it is the key every
       other piece of this row's state is stored under. Only the destination changes. */
    var _aliasCandidates = (window.RPDash && window.RPDash.getVendorAliasCandidates)
      ? window.RPDash.getVendorAliasCandidates(vendor) : [];
    var _aliasChosen = ((q && q.ordering && q.ordering[vendor]) || {}).poVendor || '';
    var _aliasAmbiguous = _aliasCandidates.length > 1;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — Alias Keywords. Before asking the admin which
       supplier fills a shared-alias row, ask the products on it: an Alias Keyword on a supplier's
       PO Profile ("dumbbell", "grip plate") names the supplier that stocks it, so
       "25 lb pair of urethane coated dumbbells" answers the question on its own.

       ⛔ THIS CARD IS WHERE THE FETCH IS KICKED, and nowhere else. It is the only surface that
       knows a shared-alias row is actually on screen; the same resolution is READ by
       resolvedVendorFor, which runs across every deal on the board and must never start a request.

       The answer is the worker's, not ours — see requestAliasAuto's header for why a matcher in
       the browser would be both blind (no line items until Order Contents loads) and a second
       opinion about where a PO goes. Until it lands, the picker renders exactly as it did before
       this existed, which is the correct resting state. */
    var _aliasAuto = null;
    if (_aliasAmbiguous && !_aliasChosen && window.RPDash && window.RPDash.getAliasAuto) {
      _aliasAuto = window.RPDash.getAliasAuto(q && q.reference, vendor);
      if (!_aliasAuto && window.RPDash.requestAliasAuto) {
        window.RPDash.requestAliasAuto(q && q.reference, vendor);
      }
    }
    var _aliasAutoVendor = (_aliasAuto && _aliasAuto.vendor) ? _aliasAuto.vendor : '';
    /* ⛔ A keyword CONFLICT is not an answer. Two suppliers' keywords on one row means the row
       holds both their goods, and picking either would send a PO for half of it to the wrong
       supplier. It gates exactly like no answer at all — but the button says which, because
       "pick one" and "your keywords disagree about this row" call for different fixes. */
    var _aliasNeedsPick = _aliasAmbiguous && !_aliasChosen && !_aliasAutoVendor;
    var _aliasResolved = _aliasAmbiguous
      ? (_aliasChosen || _aliasAutoVendor)
      : (_aliasCandidates.length === 1 ? _aliasCandidates[0] : '');

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — what this row is CALLED on screen. Ryan:
       *"when an alias is detected, I don't want the left side to still read 'Blue Sky Fitness
       Supply'."* Right — every label on this card names somebody you are about to email, chase or
       address a PO to, and on a white-label row that is never us.

       ⛔ THIS IS A LABEL AND MUST NEVER BECOME A KEY. `safeVendor` stays on every data-vendor
       attribute, because lifecycle.ordering[], freightQuotes, palletFeeKey, the PO record, the
       progress tracker and the Deal Feed are all keyed on the name off the quote line. Swap one
       data attribute to this and the row's state starts landing under a second name — a freight
       cost saved against "Ferus Prime" while the gate reads "Blue Sky Fitness Supply", and a row
       that never completes. The two variables are deliberately named for which job they do.

       Unresolved rows fall through to the row's own name, so an ordinary vendor and an
       unanswered alias both read exactly as they do today. */
    var _displayVendor = _aliasResolved || vendor;
    var safeDisplay = esc(_displayVendor);

    /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-27) - the number the Quote Workstation recorded for
       this vendor, when a rep got it from the supplier directly instead of through this card's
       request-and-save handshake. It settles the PO gate for the same reason a saved cost does:
       the gate exists so nothing goes out before we know what freight costs, and we do.

       📌 Kept SEPARATE from fqState rather than merged into it - see the header on
       `wsFreightVendors` in quote-summary-builder.js. Merging would have this card claim a request
       was sent and answered on a date nobody chose, and the card writes that map back.

       ⚠️ DECLARED HERE, ABOVE THE FREIGHT-MODE ROW, and that placement is load-bearing. It was
       first written down beside `hasCost`, ~70 lines BELOW the mode row that reads it - and because
       `var` hoists to `undefined` rather than throwing, the label silently fell through to its
       last tier instead of erroring. Same shape as the temporal-dead-zone bug in the worker
       earlier today, in the flavour that says nothing at all. */
    var _wsFreight = (function () {
      var m = (q && q.wsFreightVendors) || {};
      if (m[vendor]) return m[vendor];
      var vl = String(vendor || '').trim().toLowerCase();
      var k = Object.keys(m).find(function (kk) { return String(kk).trim().toLowerCase() === vl; });
      return k ? m[k] : null;
    })();
    var _wsFreightCost = (_wsFreight && parseFloat(_wsFreight.cost) > 0) ? parseFloat(_wsFreight.cost) : 0;
    /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - 'official' | 'estimate', carried verbatim from
       the Workstation entry by quote-summary-builder. DECLARED HERE, beside the cost, for exactly the
       reason the note above says the cost is: it is now read by freightSettled ~250 lines down as well
       as by the row that prints "(est.)", and its old home INSIDE that row would have hoisted to
       undefined at the gate and silently settled every estimate - the same failure, in the same file,
       that the note above exists to record. */
    var _wsOfficial = !!(_wsFreight && String(_wsFreight.type || '').trim().toLowerCase() === 'official');
    /* CUSTOM_FREIGHTAPPLIED_BY_CLAUDE (AIOPS `84e0f05fbcc4`, Ryan 2026-09-18) — ⛔ HAS THIS NUMBER
       REACHED THE CUSTOMER'S QUOTE? The card had the cost and the type and could not tell an
       applied figure from one merely typed, so on RW-1402-A it went on offering "Request Freight"
       beside freight that had been booked with Go Rocket and charged to the customer.

       ⛔ APPLIED ALONE DOES NOT SETTLE THE ASK, and that is Ryan's own 2026-08-29 rule standing:
       an ESTIMATE is not a firm quote, and applying it to the customer's quote does not make the
       supplier commit to it. So an applied estimate keeps the button AND keeps gating Generate PO
       — it just stops pretending nothing has happened, and says what is still owed. Asked
       directly on 2026-09-18 from a rendered before/after, he chose that over hiding the ask on
       `applied` outright.

       ⚠️ `appliedAt` IS DEAL-LEVEL, not per vendor — `wm.freight.appliedAt` is stamped when the
       freight section is applied, and the per-vendor entry carries only the boolean. On a
       multi-vendor apply that one date is right for every row it labels. */
    var _wsApplied = !!(_wsFreight && _wsFreight.applied === true);
    var _wsAppliedAt = (q && q.wsFreight && q.wsFreight.appliedAt) ? q.wsFreight.appliedAt : '';

    /* Is the carrier on that entry THIS vendor, or somebody else moving the goods for us?
       ⛔ The short code has to count. On JW-1230-G the carrier reads "CPT" - CPTurf's code - and
       treating that as a third party would print "Freight via CPT" on a row whose supplier IS
       CPTurf: a new wrong assertion in place of the old vague one, which is worse. Codes come from
       the same boot fetch as the workflow and alias maps. */
    var _wsCarrierRaw = (_wsFreight && _wsFreight.carrier) ? String(_wsFreight.carrier).trim() : '';
    var _carrierIsThisVendor = (function () {
      if (!_wsCarrierRaw) return false;
      var c = _wsCarrierRaw.toLowerCase();
      if (c === String(vendor || '').trim().toLowerCase()) return true;
      if (c === String(_displayVendor || '').trim().toLowerCase()) return true;
      var code = (window.RPDash && window.RPDash.getVendorShortCode)
        ? window.RPDash.getVendorShortCode(_displayVendor) : '';
      return !!code && c === String(code).toLowerCase();
    })();
    /* END CUSTOM_WSFREIGHT_BY_CLAUDE */

    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — the vendor is a property of the GROUP, not of
       each row. Ryan, planning a fulfillment bot: *"this will make things more clear to whoever is
       doing order fulfillment, especially an AI bot eventually."*

       Every row used to restate the supplier ("Ferus Prime freight quote", "Ferus Prime PO"), a
       compound of WHO and WHICH STEP. Splitting them makes the step names a fixed vocabulary that
       repeats identically under every vendor, so two groups can be compared by scanning one column.

       ⛔ THE SAME LAYOUT AT ONE VENDOR AND AT FOUR. A single-vendor card said "ECORE" three times;
       it now reads as one group, which is the shape multi-vendor already had. One layout to learn,
       one to maintain, and no special case for a bot to discover. That is the real win here — the
       multi-vendor tidy-up is the smaller half.

       ⚠️ DELIBERATELY NOT `.rqd-detail__section-title--sub`, which looked like the obvious reuse.
       That marker's collapse rules are scoped `.rqd-detail__arap--collapsible > …` — a DIRECT child
       of the card — and this header is a grandchild inside `.rqd-detail__arap-row`, so they would
       never have matched it. It also carries a 2px bottom border that would double up with the
       group separator directly above. Collapse still works: the card's own rule hides
       `.rqd-detail__arap-row` wholesale, header included. */
    var _groupHead = '<div class="rqd-po-group">'
      + safeDisplay
      /* Only when the two differ, so an ordinary vendor gets a clean one-line header. */
      + (_displayVendor !== vendor
          ? '<span class="rqd-po-group__alias" title="' + escAttr('Quoted as ' + vendor
              + ', bought from ' + _displayVendor) + '"> — ' + safeVendor + ' on quote</span>'
          : '')
      + '</div>';

    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — the step + state a row represents, as DATA.
       Ryan is training a bot on this surface; it will read the DOM, not the English. Labels can now
       be reworded freely without breaking anything that consumes the card, which is the whole point
       of emitting them separately. Kept alongside `data-vendor`, which remains the ROW'S OWN NAME
       (the grouping key everything is stored under) and is never the resolved supplier. */
    function stepAttrs(step, state) {
      return ' data-step="' + step + '" data-state="' + state + '"'
        + ' data-vendor="' + safeVendor + '" data-ref="' + safeRef + '"';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* Two different things, in two different places on the row (Ryan 2026-08-26): an ambiguous row
       gets a PICKER, which is an action and belongs in the right-hand button group with the other
       actions; a settled row gets a quiet note, which is information about the row's name and
       belongs beside it.

       The picker reuses `.rqd-kindselect` + `--done` rather than styling a select from scratch.
       That pair already exists for the request-kind dropdown on this same card, and is what makes a
       <select> match the 140x28 green `.rqd-invoice__generate` buttons exactly: appearance:none,
       a hand-drawn caret, and a width scaled by --rqd-sold-input-scale so the two stay matched when
       the SOLD panel rescales. Re-declaring any of that here is what made the kind dropdown drift
       to a different green once already. */
    var _aliasPickHtml = '';
    if (_aliasAmbiguous) {
      _aliasPickHtml = '<select class="rqd-kindselect rqd-kindselect--done rqd-po-vendor-pick" data-vendor="' + safeVendor + '" data-ref="' + safeRef
        + '" title="' + (_aliasAutoVendor
            ? escAttr(_aliasAutoVendor) + ' was matched automatically from the keyword “' + escAttr(_aliasAuto.keyword)
              + '” in “' + escAttr(_aliasAuto.title) + '”. Pick a supplier here to override it.'
            : 'Which supplier fills this row on this deal? The PO, the freight request and the vendor email all follow this.') + '">'
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — when a keyword has answered, the empty
           option says so instead of "Choose supplier". Value stays '' because nothing is stored:
           leaving the select alone genuinely means "let the keyword decide", and picking a name
           writes an override that outranks it. Naming the keyword and the product it matched is
           what makes the auto-pick auditable rather than magic. */
        + '<option value="">' + (_aliasAutoVendor
            ? 'Auto: ' + esc(_aliasAutoVendor)
            : 'Choose supplier') + '</option>'
        + _aliasCandidates.map(function (n) {
            return '<option value="' + esc(n) + '"' + (n === _aliasChosen ? ' selected' : '') + '>' + esc(n) + '</option>';
          }).join('')
        + '</select>';
    }
    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — the per-row "quoted as …" aside is gone; the
       group header states the alias once, which is where a fact about the SUPPLIER belongs. Its
       predecessor is worth remembering though: it was originally written as an `else if` on the
       picker branch, so it could only ever fire for a single-claimant alias and rendered nothing
       for exactly the two-claimant rows it existed for. A render harness caught that; the source
       read fine. The header below hangs off the same `_displayVendor !== vendor` test, deliberately
       not off any branch. */
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Web-order vendors
       (flagged in Vendor Guide as workflow='web-order') don't owe us a
       freight quote — admin buys direct on the vendor's website, where
       shipping is calculated at checkout. Skip the entire freight handshake
       UI for these vendors so the rep sees only the Mark Web Purchased
       button. Freight cost (when non-zero) gets entered retroactively via
       the existing freight bucket UI on the shipping side.

       (Ryan 2026-08-26) Asks about the RESOLVED supplier now, not the row name — which is why it
       sits below the alias block instead of above it. A white-label row's own name has no workflow
       of its own: the Guide answers for an alias with a single claimant, but once two suppliers
       share the name only the admin's pick can say whether this one is ordered from a website. */
    var _vWorkflowEarly = (window.RPDash && window.RPDash.getVendorWorkflow)
      ? window.RPDash.getVendorWorkflow(_aliasResolved || vendor) : '';
    var _isWebOrderVendor = _vWorkflowEarly === 'web-order';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — per-order freight-mode switch.
       For an OFFLINE deal whose vendor ships "freight-in-product" (PO would read
       "Included in product cost"), admin can flip THIS order to Blue Sky shipping
       it on our own carrier when we land a better broker rate — and record our
       freight cost (folds into the Financials full-margin GP, NOT onto the supplier
       PO). Writes lifecycle.freightOverrides[vendor] via /freight-override-set; the
       PO regenerates into BSFS-carrier mode. Gated to freight-in-product vendors
       (q.vendorShippingModels) OR an already-set override (q.freightOverrides). */
    var _lookupByVendor = function (map) {
      if (!map) return null;
      if (map[vendor] != null) return map[vendor];
      var vl = String(vendor || '').trim().toLowerCase();
      var k = Object.keys(map).find(function (kk) { return String(kk).trim().toLowerCase() === vl; });
      return k ? map[k] : null;
    };
    var _shipModel = _lookupByVendor(q.vendorShippingModels);
    var _fov = _lookupByVendor(q.freightOverrides);
    var _isOurCarrier = !!(_fov && _fov.mode === 'our-carrier');
    /* CUSTOM_THEIRCARRIER_BY_CLAUDE (Ryan 2026-09-06) — the override's SECOND mode. RW-1317-C, the
       first three-vendor deal sold through the portal: the ECORE group read "Freight via Go Rocket
       Shipping" — our broker, tier 1 of the wording rule below — and the button beside it still
       said "Our carrier". Ryan: *"if we're already using Go Rocket Shipping (our carrier),
       shouldn't the button read 'Their carrier' instead? Seems to be wired up incorrectly."*

       It was. The button only ever knew one exception (vendor default → our carrier), so on a row
       the quote had ALREADY put on our carrier it offered the state the row was in. The exception
       to a brokered row runs the other way: the supplier carries it after all. That is what
       `mode: 'vendor-carrier'` records — an explicit answer that outranks what the Workstation
       carrier field implies, the same way `our-carrier` outranks the Guide default.

       ⛔ IT IS READ BY THE SAME PAIR OF FACTS THE GATE READS. `_brokeredByUs` (~L560) is what
       settles Generate PO on a brokered row; a vendor-carrier record must un-settle it in the same
       breath, or the card says "freight by vendor" while releasing a PO that never waited for the
       vendor's freight number. Worker side, handleGeneratePo honours it too (po-routes.js
       forceVendorCarrier), so the PO prints the supplier's freight line rather than the
       BSFS-carrier instructions. */
    var _isVendorCarrier = !!(_fov && _fov.mode === 'vendor-carrier');
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM⁸) — the `q.source !== 'online'` gate is
       gone. Ryan, about to send the PO on SHOP-1922: the vendor's Guide default is "Included in
       product cost", but this shipment is one of the exceptions where we ship it ourselves — "I
       don't want to amend the Vendor Guide for one deal, just to have to change it back when
       done." That is exactly what this switch is for; it was simply never reachable from an
       online order, so the only lever left was the global vendor default.

       Nothing about the mechanism was offline-specific: /freight-override-set is keyed on
       ref+vendor, PO generation's forceOwnCarrier reads lifecycle.freightOverrides with no
       source check, and vendorShippingModels is populated by the shared builder for online rows
       too. The companion worker change makes the override survive the bucket-freight recompute
       that online orders run (see reassertOurCarrierFreight in quote-dashboard.js) — without it
       the PO would say own-carrier while the cost never reached GP.

       `_isWebOrderVendor` stays excluded: that workflow places the order on the vendor's own
       site, so there is no BSFS-carrier PO to render. */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM⁹) — the `_shipModel ===
       'freight-in-product'` requirement is gone too, and this is the reason Ryan still saw only
       "-- Select --" after the online gate came off.

       `_shipModel` reads q.vendorShippingModels[vendor], which the worker fills from the vendor
       profile's `shippingModel`. Audited all 8 profiles in KV (ANCORE, ECORE, Fabrication,
       Golden Designs, Hyperice, Perform Better, Spirit, Supply Chain): **not one of them sets
       it.** The Vendor Guide exposes the field, its default option is "standard", and nobody has
       ever saved a non-default. So this gate has been unsatisfiable for every vendor since it
       shipped 2026-06-22 — the switch was dead everywhere, not just on online orders. My earlier
       "it exists, it's just gated to offline" was half the story.

       Rather than make a per-deal exception depend on a vendor-level field nobody populates, the
       row now renders for any non-web-order vendor. That also matches what the switch is FOR:
       "there's exceptions to every rule and this is one of those" — the exception is meaningful
       whatever the vendor's default happens to be. The label reflects the real resolved default
       instead of asserting "Included in product cost" at a vendor that never said so. */
    var _showFreightSwitch = !_isWebOrderVendor;
    if (_showFreightSwitch) {
      // The freight-mode checkbox stays empty until the vendor's PO has gone out
      // — emailed (status 'sent') OR downloaded (downloadedAt) — then it checks +
      // the row goes green-done, matching the PO row's own done logic and the rest
      // of the Purchasing card's "step complete" vocabulary.
      var _fmPoOut = active.some(function (p) { return p && (p.status === 'sent' || p.downloadedAt); });
      /* CUSTOM_FOVCOSTROW_BY_CLAUDE (Ryan 2026-09-01) — ⛔ ON AN OUR-CARRIER ROW THE PO LEAVING IS
         NOT THE WHOLE STEP. The tick used to key on `_fmPoOut` alone, so the row could print
         "our cost not set" with a green check beside it — Ryan on RW-1339-A: *"the 'Freight by
         Blue Sky carrier' cost is not set yet on the Ferus Prime order, so that box for that row
         should not be checked yet."* We owe our broker a number, and the row says so; the
         checkbox was contradicting the sentence it sits on.
         ⚠️ HOISTED above `_fmCheck` — it used to be computed inside the `_isOurCarrier` branch
         BELOW, which is why the tick could not see it. The vendor-carrier row is unchanged: it has
         no cost of ours to wait on, so the PO leaving really is the whole step there. */
      var _ovrHasCost = !!(_isOurCarrier && _fov && _fov.cost != null);
      var _fmDone = _fmPoOut && (!_isOurCarrier || _ovrHasCost);
      var _fmCheck = _fmDone ? '&#10003;' : '';
      var _fmRowCls = _fmDone ? ' rqd-toggle--done' : '';
      if (_isOurCarrier) {
        var _ovrCostStr = _ovrHasCost ? (money ? money(_fov.cost) : ('$' + Number(_fov.cost).toFixed(2))) : 'not set';
        /* CUSTOM_FOVCOSTROW_BY_CLAUDE (Ryan 2026-09-01) — an UNSET cost gets the input, not a
           button that opens a popover to say the same thing.

           Switching to our carrier before the rate exists is the normal path, not an edge case:
           Ryan on RW-1339-A — *"I need to wait for the order to be completed in production, then
           the supplier to send me the weights/dims, then I'll finalize the freight quote with our
           broker, then I'll have a rate."* So the row sits at "not set" for days, and the only
           control on it was `Edit cost` wearing `.rqd-invoice__generate` — the DONE-state ghost
           variant, which in SOLD scope paints transparent-on-green. A step that has not happened
           yet was rendering in the vocabulary of a step that is finished.

           ⛔ IT REUSES THE HANDSHAKE CLUSTER, not a new control. `.rqd-freight-cost-wrap` +
           `-input-wrap` + `-prefix` + `-input` + `-save` is the same cluster the freight-quote row
           two below uses, so the dark-green fill, the 3/4-input + 1/4-floppy proportions and the
           focus ring are defined once. What differs is only WHERE the number goes: this one is our
           broker's cost (→ /freight-override-set), that one is the supplier's (→ /freight-cost-save).

           ⚠️ AND THAT IS WHY THE INPUT CARRIES `data-fov-vendor`, NOT `data-vendor`.
           DashboardHandshake.saveCost finds its input with a DOCUMENT-WIDE
           `.rqd-freight-cost-input[data-ref=…][data-vendor=…]`. Both rows render for the same
           vendor whenever a freight request is already out, and this row is emitted FIRST — so a
           `data-vendor` here would make the handshake's save read this box instead of its own and
           post our broker's number to the supplier's cost. The save handler for this row finds its
           input through the wrapper it is in, which is local by construction.

           Once a cost IS set the row is genuinely done, so `Edit cost` comes back exactly as it
           was — the ghost is right there. */
        var _ovrCostControl = _ovrHasCost
          ? '<div class="rqd-invoice">'
              + '<button class="rqd-invoice__link rqd-invoice__generate" data-freight-override-action="edit-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Edit our freight cost for this order">Edit cost</button>'
            + '</div>'
          : '<div class="rqd-freight-cost-wrap">'
              + '<div class="rqd-freight-cost-input-wrap">'
                + '<span class="rqd-freight-cost-prefix">$</span>'
                + '<input type="text" class="rqd-freight-cost-input rqd-freight-override-cost-input" data-fov-vendor="' + safeVendor + '" data-ref="' + safeRef + '" placeholder="0.00" inputmode="decimal" aria-label="Our freight cost for this order">'
              + '</div>'
              + '<button class="rqd-invoice__link rqd-freight-cost-save" data-freight-override-action="save-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Save what we pay our carrier for this order" aria-label="Save our freight cost"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg></button>'
            + '</div>';
        html += '<div class="rqd-toggle rqd-freight-mode-row' + _fmRowCls + '"' + stepAttrs('freight-mode', 'our-carrier') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">' + _fmCheck + '</div>'
          /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — names the CURRENT ARRANGEMENT rather
             than the setting. Whoever is fulfilling needs to know who is moving the goods, and
             this row is the one that says so. */
          /* CUSTOM_FOVCOSTROW_BY_CLAUDE (Ryan 2026-09-01) — the cost is SUB-TEXT, in its own
             `.rqd-toggle__date` span, because that is what it is: a detail about the arrangement
             the row names, not part of the name. Ryan: *"the font size is the same size as 'Freight
             by Blue Sky carrier' whereas normally it's a little smaller. It should match the size
             of 'requested 8/29/2026'."* That class IS what prints "requested 8/29/2026", so it
             carries the 12px muted treatment and the " – " separator with it — which also settles
             the `&middot;` this row used to hand-write, the only sub-text on the card that did. */
          + '<span>Freight by Blue Sky carrier</span>'
          + '<span class="rqd-toggle__date">our cost ' + esc(_ovrCostStr) + '</span>'
          // Revert sits inline right after "our cost $X" — same slot + slim-link
          // style the PO row's "Void" uses (left of the right-aligned action).
          + '<button class="rqd-freight-reset-link" data-freight-override-action="revert" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Revert to the vendor default (freight included in product cost)">Revert</button>'
          + _ovrCostControl
          + '</div>';
      } else {
        html += '<div class="rqd-toggle rqd-freight-mode-row' + _fmRowCls + '"' + stepAttrs('freight-mode', 'vendor-carrier') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">' + _fmCheck + '</div>'
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM⁹) — say what the vendor's default
             ACTUALLY is. Hardcoding "Included in product cost" was safe while the row only
             rendered for freight-in-product vendors; now that it renders for any vendor it would
             be stating a shipping arrangement the vendor never agreed to. Mirrors the PO's own
             resolution order (po-routes.js ~L1365). */
          /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-27) - ⛔ SAY WHAT IS KNOWN, NOT WHAT IS
             ASSUMED. This row read "Freight by vendor carrier" for every vendor whose shippingModel
             is the default - which is every vendor, since nothing in KV has ever set a non-default.
             On JW-1230-G the deal's own Workstation entry says the carrier is Go Rocket Shipping,
             OUR broker, and the row still asserted the vendor was carrying it. Ryan read it as a
             statement of fact, which is what I had made it.

             📌 Three tiers, in order of how much we actually know:
               1. a carrier named on the quote that is NOT this vendor -> name it; we are brokering
               2. a shippingModel the admin explicitly set -> state that arrangement
               3. nothing configured -> say "Vendor default", which is the honest answer

             The old wording before today was tier 3 for everyone. That was less useful but never
             wrong; asserting tier 1 or 2 without the data is worse than vague. */
          /* CUSTOM_THEIRCARRIER_BY_CLAUDE (Ryan 2026-09-06) — tier 0 sits above the three: an
             explicit vendor-carrier record names the arrangement the admin chose, whatever the
             Workstation carrier field says. And the tier-3 fallback reads "Freight by vendor"
             now, not "Freight: vendor default" — Ryan's wording, from RW-1317-C's Spirit Fitness
             group. The default IS the vendor carrying it; naming the setting instead of the
             arrangement was the odd one out on a card that names arrangements. */
          + '<span>' + (function () {
              if (_isVendorCarrier) return 'Freight by vendor';
              if (_wsCarrierRaw && !_carrierIsThisVendor) return 'Freight via ' + esc(_wsCarrierRaw);
              if (_shipModel === 'freight-in-product') return 'Freight by vendor carrier · included in product cost';
              if (_shipModel === 'parcel-only') return "Freight by vendor carrier · supplier's standard parcel rates";
              if (_shipModel === 'flat-rate') return 'Freight by vendor carrier · flat rate';
              if (_wsCarrierRaw) return 'Freight by vendor carrier';
              return 'Freight by vendor';
            })() + '</span>'
          /* CUSTOM_THEIRCARRIER_BY_CLAUDE (Ryan 2026-09-06) — a vendor-carrier record is the one
             state on this row that a click put there, so it gets the same slim Revert the
             our-carrier row has. `data-fov-mode` lets the confirm say what reverting restores:
             here it is the quote's own carrier, not "freight back into product cost". */
          + (_isVendorCarrier
              ? '<button class="rqd-freight-reset-link" data-freight-override-action="revert" data-fov-mode="vendor-carrier" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Revert to what the quote says' + (_wsCarrierRaw ? ' (freight via ' + esc(_wsCarrierRaw) + ')' : '') + '">Revert</button>'
              : '')
          + '<div class="rqd-invoice">'
            /* CUSTOM_THEIRCARRIER_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE BUTTON OFFERS THE
               EXCEPTION, NOT THE STATE. On a brokered row (a carrier on the quote that is not
               this vendor, and no record saying otherwise) the only change left to make is
               "the supplier carries it after all", so the button says "Their carrier" and
               records mode 'vendor-carrier'. Everywhere else the exception is still ours to
               take, and the button stays "Our carrier".

               ⛔ AND IT ALWAYS WEARS THE GHOST. `.rqd-invoice__generate` used to arrive only
               with `_fmDone`, so until the PO left the button sat filled forest-green — the
               card's "do this next" vocabulary — beside a row that needed nothing done. Ryan:
               *"the assumption should be that the button is correct as the default, and changing
               carrier models is the exception to the rule. So these buttons should be
               grayed/ghosted out instead of that dark forest green."* Still clickable; just not
               shouting. The tick and the row's --done class are untouched, so the step still
               reads as complete when the PO goes out. */
            + (_wsCarrierRaw && !_carrierIsThisVendor && !_isVendorCarrier
                ? '<button class="rqd-invoice__link rqd-invoice__generate" data-freight-override-action="switch-vendor" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="' + safeDisplay + ' will carry this order on their own freight, not our broker">Their carrier</button>'
                : '<button class="rqd-invoice__link rqd-invoice__generate" data-freight-override-action="switch" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Use Blue Sky\'s own carrier for this order and record our freight cost">Our carrier</button>')
          + '</div>'
          + '</div>';
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Decide whether this vendor needs a freight-quote handshake.
    // Online: Flow B only (parcel — the supplier still owes us a quote). Flow A asks for dims on
    // the Freight Balance card instead, Flow C has no supplier ask at all.
    // Offline (Ryan 2026-08-26): vendors set to Freight → PO in the Vendor Guide.
    // Web-order and our-carrier vendors skip either way — see the predicate below.
    var fqState = (q.freightQuotes || {})[vendor] || {};
    var hasRequested = !!fqState.requestedAt;
    var hasCost = fqState.cost != null && fqState.receivedAt;
    /* CUSTOM_ASKSETTLED_BY_CLAUDE (AIOPS `78ef37315ec4`, Ryan 2026-09-19) — IS THIS REQUEST STILL
       THE THING WE ARE WAITING FOR? Two signals, either one enough; the long note is on the row
       they render (State 2b, ~300 lines down).

       ⛔ DECLARED HERE, BESIDE `hasCost`, AND THAT PLACEMENT IS LOAD-BEARING — the same trap the
       `_wsFreight` note two hundred lines up records. `var` hoists to `undefined` rather than
       throwing, so these written beside the row that reads them would evaluate as falsy at the
       branch AND at any gate above it, silently restoring the bug in a way nothing errors on.

       ⛔ AND THEY MOVE NO GATE. `freightSettled` and `poGated` are untouched: an answered dims ask
       has no cost on it, and the applied-official case already settles the gate through
       `freightSettled`'s own `(_wsFreightCost > 0 && _wsOfficial)` clause. The card is explicit
       that this is a status cue, not a change to what may be purchased. */
    var _askAnsweredAt = fqState.answeredAt || '';
    var _askAppliedElsewhere = !!(_wsApplied && _wsOfficial && _wsFreightCost > 0);
    var _askSettled = hasRequested && (!!_askAnsweredAt || _askAppliedElsewhere);
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-12) — an our-carrier order owes no handshake.
       SHOP-1931: 60 ECORE rubber pavers, one of the few products whose shipping is collected in
       full at checkout ($354 — enough for the pallet almost anywhere), so Blue Sky brokers the
       pickup and the supplier never quotes freight. The order still classifies Flow B (the pavers
       carry neither the `freight-quoted` nor the `flat-rate-shipping` tag the classifier reads),
       so Generate PO sat disabled behind "Awaiting freight quote from ECORE" — a quote nobody was
       ever going to send. Switching to our carrier is precisely the statement that the supplier
       owes us nothing, so it satisfies the gate.

       Two separate effects, deliberately:
       - The REQUEST ROW disappears only when nothing was ever asked of the vendor. If a request
         is already out (or answered) and we then take the shipment ourselves, the row stays so the
         history is still on screen — hiding it would erase a real exchange with the supplier.
       - The GATE lifts either way: an our-carrier order can't be waiting on supplier freight.
       The cost stays optional — the freight row keeps reading "our cost not set" until the pickup
       is scheduled and the real number goes in via Edit cost. */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-26) — two questions, deliberately separate.

       WHICH VENDOR owes a supplier freight step is now answered once, by the shared predicate in
       dashboard-main.js, so this card and the progress bar cannot disagree — that drift is the bug
       the predicate's own 2026-08-17 note exists to record. It is also what brings OFFLINE deals
       in: a vendor set to Freight → PO in the Vendor Guide now owes the ask here too, which until
       today was true only of online Flow B orders.

       WHICH CARD renders the row stays local, because it is a layout question, not a vendor one.
       Online Flow A asks for dims on the Freight Balance card instead, and Flow C has no supplier
       ask at all — so online still narrows to Flow B here. Offline has no competing card, so any
       vendor the predicate accepts renders here.

       The falls-back-to-true on the exposed helper matters: dashboard-po-lens.js can render before
       dashboard-main.js finishes wiring window.RPDash, and losing the row is a worse failure than
       showing it — a missing row silently skips the freight step, which is the whole complaint
       this change answers. */
    var _vendorOwesFreight = (window.RPDash && window.RPDash.vendorNeedsFreightHandshake)
      ? window.RPDash.vendorNeedsFreightHandshake(q, vendor)
      : !_isWebOrderVendor;
    var _cardOwnsTheAsk = q && (q.source === 'online' ? q.onlineFlow === 'B' : true);
    var needsFreightHandshake = _vendorOwesFreight && _cardOwnsTheAsk
      && !(_isOurCarrier && !hasRequested);
    /* END CUSTOM_DEALDASHBOARD_BY_CLAUDE */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-26) — a SKIP settles the gate too. The offline
       Freight → PO block is deliberate, but a block with no way out traps a deal behind a supplier
       who never replies. `skippedAt` is a recorded decision, not a fabricated answer — deliberately
       not a $0 cost save, which would lift the gate by telling the deal freight was free and then
       overstate margin. */
    var _freightSkipped = !!fqState.skippedAt;
    /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - the trailing && _wsOfficial. The gate exists to
       stop a PO going out before we know what freight costs, and an ESTIMATE is not knowing: it is the
       number we quoted the CUSTOMER, not one the supplier committed to. Sending the PO at it is how we
       eat the difference. An official Workstation quote still settles it - that is a real supplier
       commitment recorded outside this card, which was the whole point of the 2026-08-27 change - and
       so does a cost saved through the handshake below. */
    /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND A CARRIER WE NAMED SETTLES IT TOO,
       BECAUSE THE ROW ABOVE ALREADY SAYS SO. On JW-1230-G the ECORE group printed *"Freight via Go
       Rocket Shipping"* — tier 1 of the wording rule three hundred lines up, whose whole meaning is
       "a carrier named on the quote that is NOT this vendor → we are brokering" — while the PO two
       rows below it stayed gated waiting for ECORE to quote freight. One card, two answers to
       "who is moving this?", and the admin had to tell it something it was already displaying.

       Ryan: *"with ECORE we send the PO and then ship the larger 'freight-quoted' tagged items out
       with our carrier once it's ready… I need it fixed so we can send out the PO."*

       ⛔ IT IS THE SAME PAIR OF FACTS THE ROW RENDERS FROM, deliberately — not a second rule that
       happens to agree today. If the wording rule ever stops calling this brokered, the gate stops
       treating it as settled in the same edit.

       ⚠️ `_carrierIsThisVendor` IS THE LOAD-BEARING HALF, and it already handles the short code:
       on this very deal the BSFS group's carrier reads `CPT` — CPTurf's own code — and counting
       that as a third party would settle a gate on a vendor who really is carrying their own
       goods. That is why this reuses the computed flag rather than testing the raw string.

       ⚠️ IT DOES NOT HIDE THE FREIGHT ROWS. `needsFreightHandshake` is untouched, so the request
       row, the estimate and the cost box all stay exactly where they are — you can still ask ECORE
       for weight and dims, which is a different question from who pays the truck. This only stops
       the ask BLOCKING the purchase order. */
    /* CUSTOM_THEIRCARRIER_BY_CLAUDE (Ryan 2026-09-06) — and a vendor-carrier record UN-settles it.
       The admin has said the supplier carries this one after all, so the PO owes their freight
       number again: the same "one card, two answers" trap this clause was written to close, run in
       the other direction. Same pair of facts as the wording rule, plus the record it now honours. */
    var _brokeredByUs = !!_wsCarrierRaw && !_carrierIsThisVendor && !_isVendorCarrier;
    var freightSettled = hasCost || _isOurCarrier || _brokeredByUs || _freightSkipped
      || (_wsFreightCost > 0 && _wsOfficial);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-17) — label the handshake row by what
       was ACTUALLY requested. The dims-first path (Request Dims, data-fb-action=
       'request-dims') stamps freightQuotes[vendor].kind='dims'; a parcel freight ask
       leaves kind unset. States 2 + 3 below previously hardcoded "freight quote",
       so a weight/dims request mislabeled itself as a freight request once it left
       State 1. Mirror State 1's wording off the stored kind. */
    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — the STEP, with no vendor on it; the group
       header says who. The kind distinction survives because a weight/dims ask and a freight-quote
       ask are different requests to a supplier, not two words for one thing. */
    var _reqKindLabel = (fqState.kind === 'dims') ? 'Weight & dims' : 'Freight quote';
    var _reqStepKey = (fqState.kind === 'dims') ? 'weight-dims' : 'freight-quote';
    /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - HOISTED out of State 1, otherwise unchanged. Two
       rows can now open the same ask: State 1 (nothing known) and the estimate row below (a soft number
       known, the firm one still owed). Building the button twice is how the two would drift - and one of
       them would forget the alias gate, which is the half that stops an email reaching the wrong
       supplier. */
    /* WHICH ASK this vendor gets. CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) —
       if this vendor has a freight-quoted line (per-line "freight-quoted" flag from
       the Order Contents + menu, OR a product `freight-quoted` tag), it goes through
       the dims-first broker process → "Request Dims" (data-fb-action='request-dims',
       kind='dims'). Otherwise the standard parcel "Request Freight" quote. This bridges
       the Order Contents freight-quoted conversion into the Flow B per-vendor handshake,
       which previously only ever offered "Request Freight". */
    /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-29) — the freight test runs through
       RQD.lineIsFreightQuoted so the weight rule counts here too. Without it this row could
       offer "Request Freight" on an order whose own Order Contents pill three cards up reads
       freight-quoted — two answers to one question on one screen.

       ⚠️ CALLED UNGUARDED, unlike the RPDash lookups above, and the difference is real:
       RPDash is POPULATED during main.js's async boot, so a render can genuinely beat it.
       RQD.lineIsFreightQuoted is assigned at SCRIPT-EVAL time in dashboard-shipping-cards.js,
       and all three scripts are `defer` — they run in document order, every one of them before
       main.js boots and long before anything renders. A guard here would only hide a broken
       asset load behind a wrong answer. */
    var _vendorFq = (function () {
      var items = (q && Array.isArray(q.onlineLineItems)) ? q.onlineLineItems : [];
      if (!items.length) return false;
      var vlc = String(vendor || '').trim().toLowerCase();
      var vAll = (q.financials && Array.isArray(q.financials.vendors)) ? q.financials.vendors : [];
      var singleVendor = vAll.length === 1;
      return items.some(function (it) {
        if (!it) return false;
        if (!window.RQD.lineIsFreightQuoted(it, q)) return false;
        // Single-vendor order → any freight-quoted line is this vendor's; else match vendor.
        var _itV = (window.RPDash && typeof window.RPDash.foldVendor === 'function') ? window.RPDash.foldVendor(it.vendor) : String(it.vendor || ''); // CUSTOM_ORDERSTHROUGH_BY_CLAUDE
        return singleVendor || String(_itV || '').trim().toLowerCase() === vlc;
      });
    })();
    var _reqRowLabel = _vendorFq ? 'Weight & dims' : 'Freight quote';
    var _reqRowStep = _vendorFq ? 'weight-dims' : 'freight-quote';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — a freight ask EMAILS a supplier, so it is
       gated by the same unanswered question as Generate PO: on a shared Quote Alias we do not
       yet know who to write to. The picker sits on the PO row just below. */
    /* CUSTOM_BROKEREDASKISOPTIONAL_BY_CLAUDE (Ryan 2026-09-19) — A BUILDER RATHER THAN A STRING,
       so the brokered row can dim this button without a second copy of it. The note above is
       explicit that building it twice is how the two drift and how one of them forgets the alias
       gate; a `.replace()` over the markup is the same hazard wearing a disguise, since it would
       silently no-op the day the class attribute is reordered. `extraClass` / `extraTitle` are the
       ONLY things a caller may vary — the action, the vendor, the ref and the alias gate are fixed
       here for every caller. */
    var _reqBtnHtml = function (extraClass, extraTitle) {
      var _cls = 'rqd-invoice__link' + (extraClass ? ' ' + extraClass : '');
      var _ttl = extraTitle ? ' title="' + escAttr(extraTitle) + '"' : '';
      if (_aliasNeedsPick) {
        /* ⛔ THE ALIAS GATE OUTRANKS EVERY CALLER. A shared quote name means we do not know which
           supplier to write to, so this stays hard-disabled whatever else the row wants. */
        return '<button class="rqd-invoice__link rqd-invoice__link--disabled" type="button" disabled title="'
          + esc('"' + vendor + '" is a quote name used by ' + _aliasCandidates.length
                + ' suppliers. Pick which one fills this row before requesting freight.')
          + '">' + (_vendorFq ? 'Request Dims' : 'Request Freight') + '</button>';
      }
      return _vendorFq
        ? '<button class="' + _cls + '" data-fb-action="request-dims" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;"' + _ttl + '>Request Dims</button>'
        : '<button class="' + _cls + '" data-freight-action="request-quote" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;"' + _ttl + '>Request Freight</button>';
    };
    var _reqBtn = _reqBtnHtml('', '');
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-26) — the way past the gate, as a quiet link
       rather than a button: skipping is the exception, and it should not compete for the eye
       with the action we actually want. Same treatment as the cancel / reset links below.
       ("cancel" wipes a pending request so the row asks again; this one stops asking.) */
    var _skipLink = '<button class="rqd-freight-reset-link" data-freight-action="skip" data-vendor="' + safeVendor
      + '" data-ref="' + safeRef + '" type="button" title="Send the PO without waiting for '
      + safeDisplay + ' to quote freight. Recorded on the deal — it does not enter a $0 cost.">skip</button>';
    /* END CUSTOM_DEALDASHBOARD_BY_CLAUDE */

    if (needsFreightHandshake) {
      /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - the trailing && !hasRequested. Once the formal
         ask is out this row has a better thing to say: State 2's "requested {date}" with the cost input,
         which is where the supplier's real number gets typed. Without it the estimate row would keep
         winning and there would be nowhere to enter the answer. */
      if (_wsFreightCost > 0 && !hasCost && !_freightSkipped && !hasRequested) {
        /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-27) - the number came from the quote, not from
           this card. Shown as DONE with its provenance, because "Freight quote / not requested"
           beside a deal that already carries the supplier's figure is the card contradicting the
           deal - which is exactly what sent Ryan looking.

           ⚠️ It names the SOURCE, never a fake request date. There is no requestedAt to show
           because no request was ever sent, and inventing one would put a date on the screen that
           nothing in the system justifies. */
        var _wsWho = (_wsFreight && _wsFreight.carrier) ? _wsFreight.carrier : '';
        /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - an ESTIMATE is not a settled quote. Ryan on
           RW-1339-A: *"I now need to make the formal quote request from them so I can send a PO not for
           the estimated amount but the actual amount."* The card already knew the difference - it has
           printed "(est.)" since this row shipped - it just never acted on it, so the row showed a
           finished step with nothing to click while Generate PO went live at a figure nobody committed to.

           ⛔ THE NUMBER STAYS ON THE ROW. Hiding the estimate to force the ask would be the bug this row
           was built to fix, in reverse: the card contradicting a deal that plainly carries $320. It shows
           what we have, says where it came from, and asks for the firm one.

           ⚠️ skip RIDES ALONG, not just the button. This re-gates Generate PO, and a gate with no exit
           traps the deal behind a supplier who never replies - the same reason skip exists in State 1. */
        var _wsEstOpen = !_wsOfficial;
        /* CUSTOM_FREIGHTAPPLIED_BY_CLAUDE (AIOPS `84e0f05fbcc4`, Ryan 2026-09-18) — the row now
           answers TWO questions rather than one. "Where did this number come from" was all it said;
           "has it been charged to the customer yet" is the one an operator was reading it for.

           ⛔ THE PROVENANCE WORDING IS THE ONLY THING `applied` CHANGES ON AN ESTIMATE. The check,
           the skip link, the Request button and `freightSettled` are untouched — an applied
           estimate is still an open ask and still gates the PO (Ryan 2026-08-29). It stops reading
           "from the quote (est.)", which says nothing about the $412.50 already on the customer's
           invoice, and starts saying what is true and what is still owed.

           ⛔ AN APPLIED OFFICIAL QUOTE IS A DONE STEP AND CARRIES NO EDIT. `editCost` returns
           silently unless `freightQuotes[vendor]` exists, and this row is precisely the case where
           it does not — a button that does nothing on the one card somebody opened to fix a number
           is worse than no button. The figure is edited where it lives: the Freight Quote card,
           which the title says. */
        var _wsProvenance = _wsApplied
          ? (_wsOfficial
            ? 'applied' + (_wsAppliedAt ? ' ' + formatDate(_wsAppliedAt) : '')
            : 'applied estimate — firm number still owed')
          : ('from the quote' + (_wsOfficial ? '' : ' (est.)'));
        var _wsProvTitle = 'Recorded on the quote in the Workstation'
          + (_wsWho ? ', carrier: ' + _wsWho : '')
          + (_wsOfficial ? '. Marked an official quote.' : '. Marked an estimate — a firm quote is still owed before the PO goes out.')
          + (_wsApplied
            ? ' It is applied to the customer\'s quote. Change it on the Freight Quote card.'
            : ' It is NOT applied to the customer\'s quote yet.')
          + ' No request was sent from this card.';
        html += '<div class="rqd-toggle' + (_wsEstOpen ? '' : ' rqd-toggle--done') + '"'
          + stepAttrs(_wsEstOpen ? _reqRowStep : (_wsApplied ? 'freight-cost' : _reqStepKey),
                      _wsEstOpen ? (_wsApplied ? 'applied-estimate' : 'estimate-from-quote') : (_wsApplied ? 'applied' : 'from-quote')) + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">' + (_wsEstOpen ? '' : '&#10003;') + '</div>'
          + '<span>' + (_wsEstOpen ? _reqRowLabel : (_wsApplied ? 'Freight cost' : _reqKindLabel)) + '</span>'
          + '<span class="rqd-toggle__date">' + (money ? money(_wsFreightCost) : ('$' + _wsFreightCost.toFixed(2))) + '</span>'
          + '<span class="rqd-toggle__date" title="' + escAttr(_wsProvTitle) + '">' + esc(_wsProvenance) + '</span>'
          + (_wsEstOpen ? _skipLink : '')
          + (_wsEstOpen ? '<div class="rqd-invoice">' + _reqBtn + '</div>' : '')
          + '</div>';
        /* END CUSTOM_WSFREIGHT_BY_CLAUDE */
      } else if (_freightSkipped && !hasCost) {
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-26) — a skipped ask still gets a row. The
           gate is already lifted by freightSettled, so without this the row would render State 1
           unchanged and the skip would look like it had not registered — while Generate PO
           quietly went live beside it. Undo is right here because the usual reason to skip is
           impatience, and the supplier sometimes replies the next morning. */
        var _skipDate = fqState.skippedAt ? formatDate(fqState.skippedAt) : '';
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs(_reqStepKey, 'skipped') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          + '<span>' + _reqKindLabel + '</span>'
          + '<span class="rqd-toggle__date">skipped' + (_skipDate ? ' ' + esc(_skipDate) : '') + '</span>'
          + '<div class="rqd-invoice">'
            + '<button class="rqd-freight-reset-link" data-freight-action="unskip" data-vendor="' + safeVendor
              + '" data-ref="' + safeRef + '" type="button" title="Ask ' + safeDisplay
              + ' for freight after all — restores the request row and the Generate PO gate">undo</button>'
          + '</div>'
          + '</div>';
        /* END CUSTOM_DEALDASHBOARD_BY_CLAUDE */
      } else if (!hasRequested) {
        /* CUSTOM_BROKEREDASKISOPTIONAL_BY_CLAUDE (Ryan 2026-09-19) — ⛔ WHEN THE ROW ABOVE SAYS WE
           ARE BROKERING IT, THIS ASK IS OPTIONAL, AND THE ROW SHOULD LOOK IT.
           RW-1317-C: the ECORE group's first line read "Freight via Go Rocket Shipping" and the
           line under it read "Freight quote" with a live "Request Freight" button — one card, two
           postures about a shipment whose carrier is already settled. Ryan: *"shouldn't the 2nd
           line below revert to n/a and gray out the Request Freight button?"*

           ⛔ GHOSTED, NOT DISABLED, AND THAT IS THE WHOLE INSTRUCTION. Ryan: *"ghost out the
           Request Freight button just visually, but allow us to still click it in case we want to
           compare their rate to ours."* So the button keeps its action and its pointer; only the
           paint changes. A control that looks dead and IS dead would remove the comparison.

           ⛔ THE PAINT IS `.rqd-invoice__generate` — THE CARD'S OWN DONE-STATE GHOST, not a new
           modifier. Ryan, after seeing a first cut done with a 0.45 opacity dim: *"I want it to
           look like the 'View PO' or 'Re-send PO' buttons after the PO has gone out — fill color
           the same as the background, and dark green outline/text color."* That is this class
           exactly, and in the SOLD scope it already resolves to `background: transparent` with a
           `#14532d` border and `#052e16` text. Reusing it also inherits the dark-mode and SOLD
           variants for free; an invented modifier would have had to re-state all three and would
           have drifted the first time one of them moved.

           ⚠️ AND IT IS SAFE DESPITE THE GENERIC HANDLER. dashboard-main.js has an UNFILTERED
           `e.target.closest('button.rqd-invoice__generate')` that calls `handleInvoiceGenerate`
           and returns — so painting a button with this class can, in the wrong place, generate an
           invoice instead of doing its job. It cannot here: the `[data-freight-action]` handler
           sits at ~3332, acts, and `return`s unconditionally, ~65 lines ABOVE that one. The same
           ordering is what lets "View PO" (`[data-po-action]`, ~3121) wear this class today.
           ⛔ CHECK THAT ORDERING BEFORE PUTTING THIS CLASS ON ANY OTHER CONTROL.

           ⛔ KEYED ON `_brokeredByUs`, WHICH IS THE ROW ABOVE'S OWN FLAG — not a second rule that
           agrees with it today. Ryan: *"that happens when we're clearly using our broker on the
           line above, otherwise it wouldn't get the n/a treatment or ghost out."* Same pair of
           facts the wording rule renders from (`_wsCarrierRaw && !_carrierIsThisVendor`), the
           arrangement `freightSettled` has treated as settled since 2026-08-30.
           ⚠️ `_isOurCarrier` is NOT tested here and cannot be true in this branch: an explicit
           our-carrier override with nothing requested already fails `needsFreightHandshake`, so
           that row never renders at all. Adding it would read as coverage this branch does not have.
           ⚠️ THE ASK ITSELF IS UNTOUCHED — same button, same action, same alias gate. The 2026-08-30
           decision that these rows stay ("you can still ask ECORE for weight and dims, which is a
           different question from who pays the truck") is the reason this dims rather than hides. */
        var _askOptional = _brokeredByUs;
        var _askOptionalWhy = 'Freight on this group is going via '
          + (_wsCarrierRaw || 'our carrier') + ', so ' + safeDisplay
          + ' does not need to quote it. You can still ask them if you want to compare their rate.';
        html += '<div class="rqd-toggle' + (_askOptional ? ' rqd-toggle--done' : '') + '"'
          + stepAttrs(_reqRowStep, _askOptional ? 'na-brokered' : 'not-requested') + '>'
          + '<div class="rqd-toggle__check' + (_askOptional ? ' rqd-toggle__check--na' : '') + '" style="cursor:default;"'
            + (_askOptional ? ' title="' + escAttr(_askOptionalWhy) + '"' : '') + '>'
            + (_askOptional ? 'n/a' : '') + '</div>'
          + '<span>' + _reqRowLabel + '</span>'
          + (_askOptional
              ? '<span class="rqd-toggle__date" title="' + escAttr(_askOptionalWhy) + '">not needed &middot; we are brokering</span>'
              : '')
          + (_askOptional ? '' : _skipLink)
          + '<div class="rqd-invoice">'
            + (_askOptional ? _reqBtnHtml('rqd-invoice__generate', _askOptionalWhy) : _reqBtn)
          + '</div>'
          + '</div>';
      } else if (!hasCost && !_askSettled) {
        // State 2: requested, awaiting cost. Cost input + Save. The check
        // gets the same green fill as the done state — the rep took the
        // action; we're just waiting on ECORE's reply now. Hourglass emoji
        // sized down + line-height:1 so it centers properly inside the 20px
        // checkbox (default emoji metrics push it slightly off otherwise).
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs(_reqStepKey, 'requested') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;"><span style="font-size:11px;line-height:1;">&#9203;</span></div>'
          + '<span>' + _reqKindLabel + '</span>'
          + '<span class="rqd-toggle__date">requested ' + formatDate(fqState.requestedAt) + '</span>'
          + '<button class="rqd-freight-reset-link" data-freight-action="cancel" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Cancel — wipes the pending freight request for this vendor and reverts the row to State 1">cancel</button>'
          /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) — SHOP-1999's row: "freight quote ·
             requested 8/28/2026 · cancel" on an email still sitting in the queue, with nothing
             saying so and no way to hurry it. Renders only while the hold is real and in the
             future, so a normal sent request looks exactly as it did. */
          + window.RQD.scheduledReleaseBtnHtml({
              kind: 'freight', ref: safeRef, vendor: vendor,
              type: (fqState.kind === 'dims' ? 'dims' : 'freight'),
              scheduledFor: fqState.scheduledFor,
            })
          + '<div class="rqd-freight-cost-wrap">'
            + '<div class="rqd-freight-cost-input-wrap">'
              + '<span class="rqd-freight-cost-prefix">$</span>'
              + '<input type="text" class="rqd-freight-cost-input" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" placeholder="0.00" inputmode="decimal">'
            + '</div>'
            + '<button class="rqd-invoice__link rqd-freight-cost-save" data-freight-action="save-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Save freight cost" aria-label="Save freight cost"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg></button>'
          + '</div>'
          + '</div>';
      } else if (!hasCost) {
        /* CUSTOM_ASKSETTLED_BY_CLAUDE (AIOPS `78ef37315ec4`, Ryan 2026-09-19) — ⛔ STATE 2b: THE
           ASK WENT OUT AND IS NO LONGER WHAT WE ARE WAITING FOR.
           ─────────────────────────────────────────────────────────────────────────────────────
           RW-1349-A. The weight/dims ask left for ECORE on 1 Sep at 17:03; GoRocket's official
           $460.51 was applied to the quote at 19:29 THE SAME DAY, and ECORE's dims reply landed
           eighteen days later on the 19th. Through all of it the row above read
           "⏳ Weight & dims · requested 9/1/2026", because State 2 asks only whether a COST has
           been saved into `freightQuotes` — and on an offline deal the number never lands there
           (quote-summary-builder's `lc.freightQuotes[v].cost` sum runs inside the `online` block;
           the Workstation writes `wm.freight`). The only control that took the row down was
           `cancel`, which DELETES the entry, so Ryan traded away an eighteen-day ask log to clear
           a cue about work that was finished.

           TWO SIGNALS, EITHER ONE ENOUGH, and both are read from where the fact already lives:
             answered  `fqState.answeredAt` — stamped by lib/ask-answered.js when a settling reply
                       from that vendor's own domain is filed. The number may still need typing,
                       so the cost box STAYS.
             applied   the Workstation's freight for this vendor is APPLIED and OFFICIAL. Then the
                       figure is on the customer's quote and there is nothing to type, so the box
                       goes. ⛔ An applied ESTIMATE is NOT this — Ryan's 2026-08-29 rule, reaffirmed
                       on 2026-09-18: an estimate is not a supplier commitment, it still gates the
                       PO, and it still counts as waiting.

           ⛔ THE REQUESTED DATE STAYS ON THE ROW. The card's own instruction is that this is a
           status cue, not a deletion: the ask log is history somebody will want when a supplier
           says they were never asked.
           ⛔ AND NO `cancel` HERE. That link's whole job is to withdraw a request we are still
           waiting on; offering it beside a row that says the answer arrived is offering to erase
           the record of a completed exchange, which is exactly what happened on 19 Sep. */
        var _settledWord = _askAnsweredAt
          ? 'answered ' + formatDate(_askAnsweredAt)
          : ('freight applied' + (_wsAppliedAt ? ' ' + formatDate(_wsAppliedAt) : ''));
        var _settledTitle = _askAnsweredAt
          ? ((_displayVendor || 'The supplier') + ' replied to this request, so the card has stopped waiting on it. The request itself is kept on the deal. Enter their figure here if there is one.')
          : ('Freight for ' + safeDisplay + ' is already on the customer’s quote as an official figure, so nothing is waiting on this request. It is kept on the deal as a record that it went out.');
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs(_reqStepKey, _askAnsweredAt ? 'answered' : 'freight-applied') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          + '<span>' + _reqKindLabel + '</span>'
          + '<span class="rqd-toggle__date">requested ' + formatDate(fqState.requestedAt) + '</span>'
          + '<span class="rqd-toggle__date" title="' + escAttr(_settledTitle) + '">' + esc(_settledWord) + '</span>'
          /* The box only where a number is still owed — see the note above. */
          + (_askAnsweredAt
              ? '<div class="rqd-freight-cost-wrap">'
                  + '<div class="rqd-freight-cost-input-wrap">'
                    + '<span class="rqd-freight-cost-prefix">$</span>'
                    + '<input type="text" class="rqd-freight-cost-input" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" placeholder="0.00" inputmode="decimal">'
                  + '</div>'
                  + '<button class="rqd-invoice__link rqd-freight-cost-save" data-freight-action="save-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Save freight cost" aria-label="Save freight cost"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg></button>'
                + '</div>'
              : '')
          + '</div>';
        /* END CUSTOM_ASKSETTLED_BY_CLAUDE */
      } else {
        // State 3: cost saved. Done. Inline meta uses .rqd-toggle__date
        // throughout (italic muted with " – " prefix) so cost + date
        // read consistently — was a smaller mono via .rqd-po-num.
        // Edit button gets the green .rqd-invoice__generate variant
        // since this step is complete (matches Flow A pattern).
        /* CUSTOM_FULFILSTEPS_BY_CLAUDE (Ryan 2026-08-27) — once a COST is saved this row is the
           freight cost, so it says so. It used to keep the ask's label ("Freight quote") after the
           number landed, which made the vocabulary flip depending on whether the separate Freight
           cost row below was showing — the same step reading two different ways on one card.
           `freight-cost:recorded` pairs with `freight-cost:not-recorded` on that row. */
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs('freight-cost', 'recorded') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          + '<span>' + ((fqState.kind === 'dims') ? 'Weight &amp; dims cost' : 'Freight cost') + '</span>'
          + '<span class="rqd-toggle__date">' + (money ? money(fqState.cost) : ('$' + Number(fqState.cost).toFixed(2))) + '</span>'
          + '<span class="rqd-toggle__date">' + formatDate(fqState.receivedAt) + '</span>'
          + '<button class="rqd-freight-reset-link" data-freight-action="reset" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Reset — clears the entire freight handshake (request + cost) for this vendor">reset</button>'
          + '<div class="rqd-invoice">'
            + '<button class="rqd-invoice__link rqd-invoice__generate" data-freight-action="edit-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;">Edit</button>'
          + '</div>'
          + '</div>';
      }
    }

    // Generate PO is gated until the freight side is actually settled.
    //   Flow B: vendor needs a freight quote → wait for cost to land
    //           (parcel — supplier owes us a quote first).
    //   Flow A: freight invoice was emailed AND the balance is paid
    //           (customer needs to pay before we PO ECORE — otherwise
    //           we're committing to production with no freight money in).
    //   Flow C / offline: no extra gate beyond existing logic.
    var isFlowA = q && q.source === 'online' && q.onlineFlow === 'A';
    var fbEmailed = !!(q && q.freightBalance && q.freightBalance.emailedAt);
    var fbPaid = !!(q && q.balanceCollected && q.balanceCollected.paid);
    // Flow A balance is considered "handled" when any of these is true:
    //   1. fb.handledExternally — explicit admin override flag
    //   2. q.balanceChildren has entries — admin linked a separate Shopify
    //      order (via "Link Balance Order") that represents the freight
    //      payment. Auto-detection: no override needed in this case.
    //   3. fbEmailed && fbPaid — normal Flow A invoice path completed.
    var hasLinkedBalanceChild = !!(q && Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0);
    var fbHandledExt = !!(q && q.freightBalance && q.freightBalance.handledExternally);
    var fbHandled = fbHandledExt || hasLinkedBalanceChild;
    var poGatedFlowA = isFlowA && !fbHandled && (!fbEmailed || !fbPaid);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Balance Reconciliation hard-block.
       When parent has a linked balance child (q.balanceChildren), the
       full child amount must be allocated across qty deltas + freight
       bucket before Generate PO is allowed — otherwise we'd PO a vendor
       for the wrong material qty or skip freight that the customer paid
       for. q.balanceReconciliation is computed worker-side; balanced
       means remaining is effectively $0 (within ½¢). */
    var balanceUnreconciled = hasLinkedBalanceChild
      && q.balanceReconciliation
      && q.balanceReconciliation.balanced === false;
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* `freightSettled` (not `hasCost`) — an our-carrier order is settled without a supplier quote.
       The Flow A balance gate is untouched on purpose: it protects the customer's freight money
       being collected before we commit the vendor to production, which is a different question
       from who carries the shipment. */
    /* CUSTOM_FULFILSTEPS_BY_CLAUDE (Ryan 2026-08-27) — the two fulfillment steps that existed only
       on ONLINE deals, brought onto the offline Purchasing card.

       Ryan, comparing SHOP-1976's FULFILLMENT card to this one: *"I love the way this PURCHASING
       card looks so far, it's just missing these fields which are important to the fulfillment
       flow."* Online Flow A has sent the PO and is still waiting on Weight/dims received and
       Freight cost — which is the sequence he wants offline too: *"I'd send the PO now, then enter
       the freight cost and weight/dims received later when I have them."*

       ⛔ NOTHING NEW IS BUILT HERE. Both controls already exist and are already extracted:
         - the dims form is `RQD.renderFreightBalancePalletForm(ref, existing, vendor)`, which
           already supports the PER-VENDOR path (`data-vendor` → lifecycle.ordering[v].freightDims)
         - the cost cluster is this file's own `.rqd-freight-cost-wrap`, posting `save-cost` →
           /freight-cost-save → freightQuotes[vendor]
       They were simply on a different card. The triggers below reuse the SAME `data-fb-action`
       values the Tracking card uses, so one handler serves both surfaces.

       📌 WHY THE DIMS ROW MATTERS MORE THAN IT LOOKS. Ryan: *"once we've got enough data to go off
       of, we'll be able to extrapolate what a packaged 5-50 lb set of dumbbells looks like … then
       we can start being one step ahead in that we won't need to request weight/dims anymore, they
       can autofill and we can just send the PO straight up."* Saving here writes a sample to
       `freight-samples:{handle||sku}` and recomputes the profile. ⚠️ Measured 2026-08-27: that
       corpus holds ZERO samples — the loop has never once closed, because this capture only ever
       existed on a card gated behind a PO that could not be sent.

       ⚠️ OFFLINE ONLY. Online Flow A renders both rows in its own bucket panel; adding them here
       would print each step twice on the same deal. */
    if (q && q.source !== 'online' && !_isWebOrderVendor) {
      var _ordV = (q.ordering || {})[vendor] || {};
      var _dimsRaw = _ordV.freightDims || null;
      /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-30) — ⛔ WHO IS CARRYING IT DECIDES WHETHER THESE
         TWO STEPS EXIST. Until now the only gate on this block was "offline and not a web order",
         so every offline vendor got both rows whatever the shipping arrangement.

         JW-1230-G's CPTurf half made it plain. CPTurf is `po-only` in the Guide — it owes no
         freight handshake, which is why that group correctly shows no Freight quote row — and its
         goods go out on the vendor's own carrier at an OFFICIAL $230.12 already recorded on the
         quote. It still rendered *"Weight/dims received"* and a *"Freight cost"* row with an empty
         box to type into. Ryan: *"why would we be requesting weight/dims on something that is
         going out by our vendors carrier?"* and *"if we were already given this, why would we
         need/want to enter it again now?"* Two rows, two questions, and neither had an answer.

         📌 DIMS ARE FOR A PALLET WE WILL ACTUALLY HANDLE. The row exists to feed
         `freight-samples:{handle||sku}` so a broker quote can eventually autofill — worth having,
         and untouched wherever it applies. But a shipment leaving on the supplier's own truck
         never puts a pallet in front of us: nobody measures it, no broker is booked, and the step
         sits open forever looking owed.

         ⚠️ HISTORY IS NEVER HIDDEN. Dims already logged, or explicitly marked N/A, keep their row
         no matter who carries the shipment — the same rule the freight REQUEST row follows a few
         hundred lines up ("hiding it would erase a real exchange"). This can only ever remove a
         row that is blank AND inapplicable. */
      /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-09-01) — ⛔ AND A FREIGHT ASK IS NOT A DIMS ASK.
         The 2026-08-30 gate above removed this row for a vendor who owes NO freight step, and left
         it standing for any vendor who owes one — using "owes a freight step" as the proxy for "we
         will be handling this pallet". Those are not the same question, and the card already knows
         the difference two hundred lines up, where the same pair of facts picks the request row's
         label:

           kind 'dims'   (Request Dims)    → WE broker it → we need weight/dims for our broker → ask
           kind 'freight' (Request Freight) → the SUPPLIER prices their own carriage → nobody hands
                                              us a pallet, and nobody sends dims either

         RW-1339-A's TKO group is `kind: 'freight'` — FL Fit Group quoted $850 and TKO's truck takes
         it — and the card still printed "Weight/dims received" with a Log dims button. Ryan: *"why
         on the TKO section does it have a 'Weight/dims received' section when we never requested
         weight/dims"* — the same complaint JW-1230-G's CPTurf half drew, one rung further in.

         📌 IT COSTS NO CAPTURE. The freight-samples corpus this row feeds only ever fills from the
         dims-first path: a plain freight ask comes back as a price, so on those vendors the row was
         not an unused opportunity, it was a box nothing could ever fill.

         ⚠️ AN UNSET `kind` IS A FREIGHT ASK, by the same rule the label reads (see _reqKindLabel):
         the dims path stamps 'dims', a parcel ask leaves it unset. So legacy records fall to the
         freight side, which is what they were.

         ⚠️ `_vendorFq` COVERS THE NOT-YET-ASKED STATE, where there is no `kind` to read but the row
         is already offering "Request Dims" — without it the dims row would vanish right up until
         the ask goes out and then reappear. It is false on offline deals by construction (it reads
         onlineLineItems), which is correct: an offline deal's dims-first ask is a deliberate act. */
      var _handshakeIsDimsAsk = needsFreightHandshake
        && (fqState.kind === 'dims' || (!hasRequested && _vendorFq));
      var _weHandleThePallet = _brokeredByUs || _isOurCarrier || _handshakeIsDimsAsk;
      var _dimsPallets = null;
      if (_dimsRaw && typeof _dimsRaw === 'object') {
        _dimsPallets = (Array.isArray(_dimsRaw.groups) && _dimsRaw.groups.length > 0)
          ? _dimsRaw.groups
          : (Array.isArray(_dimsRaw.pallets) ? _dimsRaw.pallets : null);
      }
      var _dimsLogged = Array.isArray(_dimsPallets) && _dimsPallets.length > 0;
      var _dimsNa = !!(_dimsRaw && _dimsRaw.na);
      var _dimsSummary = (_dimsRaw && _dimsRaw.summary) || '';

      /* The gate, stated once: applicable, or already answered. */
      var _showDims = _weHandleThePallet || _dimsLogged || _dimsNa;

      if (!_showDims) {
        /* Nothing — the supplier's own carrier is moving this and no dims were ever recorded. */
      } else if (_dimsNa) {
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs('weight-dims-received', 'na') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          + '<span>Weight/dims received</span>'
          + '<span class="rqd-toggle__date" title="' + escAttr('No weight/dims will be received for this shipment') + '">N/A'
            + ((_dimsRaw && _dimsRaw.reason) ? ' &middot; ' + esc(_dimsRaw.reason) : '') + '</span>'
          + '<div class="rqd-invoice">'
            + '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="dims-na-clear" data-ref="' + safeRef + '" data-vendor="' + safeVendor + '" type="button" title="Undo N/A — reopen weight/dims entry">Undo</button>'
          + '</div>'
          + '</div>';
      } else if (_dimsLogged) {
        var _dimsDisplay = _dimsSummary || (_dimsPallets.length + ' pallet' + (_dimsPallets.length === 1 ? '' : 's'));
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs('weight-dims-received', 'received') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          + '<span>Weight/dims received</span>'
          + '<span class="rqd-toggle__date" title="' + escAttr(_dimsSummary || _dimsDisplay) + '">&#128230; ' + esc(_dimsDisplay) + '</span>'
          + '<div class="rqd-invoice">'
            + '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="edit-vendor-dims" data-ref="' + safeRef + '" data-vendor="' + safeVendor + '" type="button">Edit</button>'
          + '</div>'
          + '</div>';
      } else {
        html += '<div class="rqd-toggle"' + stepAttrs('weight-dims-received', 'not-received') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;"></div>'
          + '<span>Weight/dims received</span>'
          + '<div class="rqd-invoice">'
            + '<button class="rqd-invoice__link" data-fb-action="add-vendor-dims" data-ref="' + safeRef + '" data-vendor="' + safeVendor + '" type="button" style="cursor:pointer;" title="Log pallet count, weight and dims for this vendor. Stored per product so freight can eventually be estimated without asking.">Log dims</button>'
          + '</div>'
          + '</div>';
      }
      /* The form expands into this block, exactly as it does on the Tracking card — same class,
         same handler, so `handleVendorDimsOpen` needs no knowledge of which card opened it.
         ⚠️ Gated with the row: with no dims row there is no trigger, so an ungated block would be
         an empty expander nothing can ever open. */
      if (_showDims) {
        html += '<div class="rqd-vendor-dims-block" data-ref="' + safeRef + '" data-vendor="' + safeVendor + '"></div>';
      }

      /* ⛔ FREIGHT COST IS ITS OWN STEP, and it is NOT the freight QUOTE above it. The quote is what
         the supplier estimated; this is what we actually pay once the pickup is scheduled — Ryan:
         *"the freight cost box where we can enter in our true cost once we schedule the actual
         pickup in a month or two once production is ready."*

         Suppressed when the handshake already holds a saved cost, because that row IS this number
         and printing it twice is the redundancy this card is being cleaned up to remove. */
      /* CUSTOM_WSFREIGHT_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND SUPPRESSED AGAIN WHEN THE FIGURE IS
         ALREADY FINAL. Ryan on JW-1230-G's CPTurf half: *"if we were already given this, why would
         we need/want to enter it again now?"*

         That sentence above — "once the pickup is scheduled in a month or two" — describes a
         shipment WE are booking. It is the wrong picture for a vendor-carrier order whose supplier
         has already committed to a price: CPTurf's $230.12 is `type: 'official'` on the quote, so
         there is no later number to discover and nothing to type. The row still asked, with an
         empty box, next to the figure it was asking for.

         ⚠️ `_wsOfficial` IS THE SAME TEST `freightSettled` USES, deliberately. An ESTIMATE keeps
         the input — that is precisely the case where the real number is still to come, and the
         2026-08-29 rule already says an estimate is not knowing. So this narrows to the one state
         where the answer exists: a supplier commitment recorded on the quote.

         📌 THE ROW STAYS, AS A DONE STEP. Hiding it would remove freight cost from a fulfillment
         card whose job is to show every step — and the number is worth reading. It just stops
         pretending to be an outstanding task. */
      var _costIsFinal = _wsOfficial && _wsFreightCost > 0;
      /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE FREIGHT QUOTE CARD NOW OUTLIVES
         THE SALE, SO THIS ROW HANDS IT THE PEN.

         That card used to be gated on the inverse of this one — sold, and it vanished — which is
         the moment the carrier's real rate actually arrives. It stays now, and the invariant the
         two gates existed to protect ("a deal can never show the same freight step twice") is kept
         HERE instead: while that card is on screen it owns the cost box, because its box is the one
         with markup and charged beside it.

         ⛔ AND THAT MATTERS BEYOND TIDINESS. This row posts a bare `{quoteRef, vendor, cost}`;
         `/freight-cost-save` reads an absent `charged` as "leave it" and re-derives `markedUpTotal`
         from the stored markup — so typing the true cost HERE silently moves what the customer is
         billed. The card's charged box back-solves the markup and sets `_chargedOverride`, which is
         Ryan's "keep". Two boxes for one number, one of which quietly re-prices the customer, is
         not a duplicate — it is a trap.

         ⚠️ THE ROW STAYS AND STILL SHOWS THE NUMBER. Removing it would take freight cost out of the
         fulfilment sequence entirely; the step is real, it is just answered elsewhere now. */
      var _cardsOwnCost = !!(window.RQD && typeof window.RQD.dealLogisticsCardsApply === 'function'
        && window.RQD.dealLogisticsCardsApply(q));
      if (!hasCost && (_costIsFinal || _cardsOwnCost)) {
        var _fcVal = _wsFreightCost > 0
          ? (money ? money(_wsFreightCost) : ('$' + _wsFreightCost.toFixed(2)))
          : 'not set';
        var _fcDone = _costIsFinal || _wsFreightCost > 0;
        html += '<div class="rqd-toggle' + (_fcDone ? ' rqd-toggle--done' : '') + '"'
            + stepAttrs('freight-cost', _costIsFinal ? 'official' : 'on-freight-card') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">' + (_fcDone ? '&#10003;' : '') + '</div>'
          + '<span>Freight cost</span>'
          + '<span class="rqd-toggle__date" title="'
            + escAttr(_costIsFinal
                ? 'An official freight figure on the quote — the supplier has committed to it, so there is nothing to enter later.'
                : 'Recorded on the Freight Quote card above. Update it there when the carrier\'s real rate lands, so the charged amount and the margin stay in step.')
            + '">' + _fcVal + (_costIsFinal ? ' &middot; official' : '') + '</span>'
          + '</div>';
      } else if (!hasCost) {
        var _fcKnown = _wsFreightCost > 0;
        html += '<div class="rqd-toggle"' + stepAttrs('freight-cost', 'not-recorded') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;"></div>'
          + '<span>Freight cost</span>'
          + (_fcKnown
              ? '<span class="rqd-toggle__date" title="' + escAttr('Quoted on the deal. Enter what we actually pay once the pickup is scheduled.')
                + '">quoted ' + (money ? money(_wsFreightCost) : ('$' + _wsFreightCost.toFixed(2))) + '</span>'
              : '')
          + '<div class="rqd-freight-cost-wrap">'
            + '<div class="rqd-freight-cost-input-wrap">'
              + '<span class="rqd-freight-cost-prefix">$</span>'
              + '<input type="text" class="rqd-freight-cost-input" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" placeholder="0.00" inputmode="decimal">'
            + '</div>'
            + '<button class="rqd-invoice__link rqd-freight-cost-save" data-freight-action="save-cost" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" style="cursor:pointer;" title="Save what we actually pay for freight on this order" aria-label="Save freight cost"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg></button>'
          + '</div>'
          + '</div>';
      }
    }
    /* END CUSTOM_FULFILSTEPS_BY_CLAUDE */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — _aliasNeedsPick joins the gate: a PO for a
       shared quote name has no addressee yet, and the worker refuses it anyway. Gating here means
       the admin reads the reason on the button instead of a 400 after the click. */
    var poGated = _aliasNeedsPick || (needsFreightHandshake && !freightSettled) || poGatedFlowA || balanceUnreconciled;
    var poGateReason;
    if (_aliasNeedsPick) {
      poGateReason = '"' + vendor + '" is a quote name used by ' + _aliasCandidates.length
        + ' suppliers (' + _aliasCandidates.join(', ') + ')'
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-27) — a conflict earns its own sentence. The
           fix for "no keyword matched" is to add one; the fix for "two matched" is usually to
           split the row, and sending the admin to the Vendor Guide for that would waste a trip. */
        + (_aliasAuto && _aliasAuto.reason === 'conflict'
            ? ', and the products on this row match keywords from more than one of them'
            : '')
        + '. Pick which one fills this row first.';
    } else if (balanceUnreconciled) {
      var _rem = q.balanceReconciliation.remaining || 0;
      poGateReason = _rem > 0
        ? 'Linked balance is short by $' + Math.abs(_rem).toFixed(2) + ' — allocate the remainder via qty adjustments or the freight bucket on the Linked Balance Reconciliation card.'
        : 'Linked balance is over-applied by $' + Math.abs(_rem).toFixed(2) + ' — reduce qty adjustments or freight allocation on the Linked Balance Reconciliation card.';
    } else if (poGatedFlowA) {
      poGateReason = !fbEmailed
        ? 'Send the freight balance email to ' + safeDisplay + ' first.'
        : 'Awaiting customer payment on the freight balance.';
    } else if (_wsFreightCost > 0 && !_wsOfficial && !hasRequested) {
      /* CUSTOM_WSESTIMATE_BY_CLAUDE (Ryan 2026-08-29) - name the number. "Awaiting freight quote" beside
         a row that plainly shows $320 reads as though the card cannot see it. */
      poGateReason = 'Only an estimate (' + (money ? money(_wsFreightCost) : ('$' + _wsFreightCost.toFixed(2)))
        + ') is on the quote — request a firm ' + (_vendorFq ? 'weight/dims figure' : 'freight quote')
        + ' from ' + _displayVendor + ', or skip to send the PO anyway.';
    } else {
      poGateReason = 'Awaiting ' + (fqState.kind === 'dims' ? 'weight/dims' : 'freight quote') + ' from ' + _displayVendor;
    }

    if (active.length) {
      var p = active[active.length - 1];
      var sent = p.status === 'sent';
      /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-13) — SCHEDULED state. The PO was composed with
         "Send within business hours" ticked outside 6am-6pm MT, so the email is queued and the
         cron sends it at the window open. Ryan sent SHOP-1923-LF this way and had no way to tell
         it had worked: the row looked exactly like an unsent PO.
           * Email PO  -> ghosted, reads "Scheduled", disabled (clicking would queue a SECOND copy)
           * Mark Sent -> hidden; there is nothing to record about a send that has not happened
           * check     -> hourglass, the same U+23F3 this file already uses for "requested, waiting"
         Deliberately NOT counted as done: the vendor does not have it yet. */
      var scheduled = p.status === 'scheduled';
      var schedLabel = scheduled && p.scheduledFor ? _fmtSched(p.scheduledFor) : '';
      /* CUSTOM_POSEND_BY_CLAUDE (AIOPS suggestion ea3ab4a798eb, Ryan 2026-09-02) — ⛔ A PO AN AGENT
         QUEUED LOOKS EXACTLY LIKE A PO A HUMAN QUEUED, AND THEY ARE NOT THE SAME EVENT.
         `scheduled` has meant one thing since the send window shipped: a rep ticked "send within
         business hours". It now also means "Elon composed this and it is inside its undo window",
         and the difference is the whole reason the hold exists — the question a person opens this
         card to answer is *did a bot just decide to order from a supplier, and can I still stop
         it?* Rendering both as a plain hourglass answers neither.
         ⚠️ AND AN APPROVAL HOLD HAS NO CLOCK AT ALL. `scheduledFor` is null for one, so every
         label below has to read correctly with no time in it — `scheduledReleaseBtnHtml` already
         returns nothing without a future timestamp, and the fallback pill used to interpolate an
         empty string into "sends at ." Ryan's standing rule for agent work is that a human can SEE
         it; a blank where the time should be is the opposite. */
      var heldBy = scheduled ? (p.heldForAgent || '') : '';
      var awaitingApproval = scheduled && (p.holdMode === 'approval' || !p.scheduledFor);
      var heldWord = !heldBy ? ''
        : (awaitingApproval ? 'Held for your approval — queued by ' + heldBy
                            : 'Queued by ' + heldBy + ', still inside its undo window');
      // "Done" = either the PO was emailed OR the rep downloaded the PDF
      // (downloadedAt). Both are explicit acts of finishing with the PO; the
      // checkbox flips for either.
      /* ⚠️ `done` also counts a DOWNLOADED pdf, so a scheduled PO whose PDF someone opened would
         otherwise render the green ✓ and the done row styling — reading as "sent" on the very row
         that exists to say it has not been. Scheduled outranks it: the vendor does not have it. */
      var done = (sent || !!p.downloadedAt) && !scheduled;
      /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-14) — the row still takes .rqd-toggle--done while
         scheduled, because that class is what paints the check green (.rqd-toggle--done
         .rqd-toggle__check, ~L6532) and Ryan wants the hourglass sitting in a filled green box:
         "it should fill with green when it includes the hourglass".
      
         That is also the house pattern already in this file — the freight "requested, waiting" row
         uses --done WITH an hourglass. So on this dashboard --done means "this step is handled",
         and the ICON says whether it is finished or still in flight.
      
         `done` stays FALSE while scheduled, deliberately, because it also drives the button styling:
         the Email/Re-send button must not take the sent-green ghost on a PO that has not gone out. */
      var rowDone = done || scheduled;
      // Date shown: prefer sentAt, fall back to downloadedAt, then created.
      var dateVal = p.sentAt || p.lastSentAt || p.downloadedAt || p.createdAt || p.generatedAt;
      // PO number + date both use .rqd-toggle__date for consistent
      // italic-muted treatment with " – " prefixes (was rqd-po-num /
      // smaller-monospaced before — broke the row's visual rhythm).
      // Action buttons get the green .rqd-invoice__generate variant
      // when the PO is done (sent or downloaded) so the row reads as
      // complete; Void stays red since it's a destructive action.
      var poDoneClass = done ? ' rqd-invoice__generate' : '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — HAS THE ORDER MOVED SINCE THIS PO WENT OUT?
         `q.poDrift[poNumber]` is computed worker-side (lib/po-order-drift.js) by diffing the order
         quantities this PO was generated from against the order's quantities now. Present only when
         they actually disagree, so the chip's existence IS the warning.

         Why it exists: SHOP-1941-ECORE went to ECORE West for $1,530.03 against a $1,043.72 order,
         carrying 37 transition strips the customer had removed four days earlier, and nothing on
         this screen said so. An audit found a second that had been wrong for a MONTH. The
         supplier-facing document is the one place in the portal where being quietly out of date
         costs real money, so it gets the loudest thing on the row.

         Placed next to Void deliberately — the fix for a drifted PO is void, regenerate, re-send,
         and the button that starts that is right there. */
      var _drift = (q && q.poDrift && q.poDrift[p.poNumber]) || null;
      var _driftChip = '';
      if (_drift && _drift.length) {
        var _driftTxt = _drift.map(function (c) {
          return (c.sku || 'line') + ' ' + c.was + ' → ' + c.now;
        }).join(', ');
        _driftChip = '<span class="rqd-po-drift" title="'
          + esc('This PO no longer matches the order: ' + _driftTxt
              + '. Void it, regenerate, and re-send so the supplier ships what was actually bought.')
          + '">&#9888; Order changed: ' + esc(_driftTxt) + '</span>';
      }
      html += '<div class="rqd-toggle' + (rowDone ? ' rqd-toggle--done' : '') + (_drift && _drift.length ? ' rqd-toggle--drift' : '') + '"'
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) — ⛔ `sent` WAS TWO DIFFERENT FACTS
           WEARING ONE WORD. A real Email PO and "Mark as Sent" (which mails nothing — it records
           a PO delivered by phone or another channel) both wrote `status: 'sent'`, and this row
           rendered identically for both. So nothing on the card could answer the one question
           that matters before chasing a supplier: does the vendor actually have this?

           ⛔ SPLIT IN `data-state` ITSELF, not bolted on as a second attribute. A reader that
           checks only `data-state` must be forced to confront the difference — leaving the token
           as `sent` and hiding the truth in a sibling attribute reproduces the original bug for
           anything that does not know to look for it. */
        /* CUSTOM_POSEND_BY_CLAUDE (Ryan 2026-09-02) — `scheduled-agent` and `scheduled-approval`
           split off `scheduled` for the same reason `sent` was split into sent-emailed and
           sent-recorded above, and by the same method: IN `data-state` ITSELF. A reader that checks
           only this attribute must be made to confront the difference between "a rep ticked
           business hours", "a bot queued this and the clock is running" and "a bot queued this and
           nothing happens until somebody says yes". Hiding that in a sibling attribute would
           reproduce the original bug for everything that does not know to look. */
        + stepAttrs('purchase-order', sent ? (p.sentExternally ? 'sent-recorded' : 'sent-emailed')
            : (scheduled ? (heldBy ? (awaitingApproval ? 'scheduled-approval' : 'scheduled-agent') : 'scheduled')
                         : (done ? 'downloaded' : 'draft'))) + '>'
        + '<div class="rqd-toggle__check" style="cursor:default;">' + (done ? '&#10003;' : (scheduled ? '<span style="font-size:11px;line-height:1;">&#9203;</span>' : '')) + '</div>'
        + '<span>Purchase order</span>'
        /* The one thing a person needs off this row when a bot is involved: who queued it, and
           whether it is still stoppable. Silent for every human-scheduled PO. */
        + (heldBy
            ? '<span class="rqd-toggle__date" title="' + escAttr(heldWord
                + (schedLabel ? '. Sends ' + schedLabel + ' unless it is discarded first.'
                              : '. Nothing goes to the supplier until an admin releases it.'))
              + '">&#129302; ' + esc(heldBy) + (awaitingApproval ? ' &mdash; needs approval' : ' &mdash; undo window') + '</span>'
            : '')
        /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — ⛔ THE ONE ROW THAT MAY CONTRADICT ITS
           OWN HEADER. `p.poVendor` is stamped onto the PO record at generation time, so if the
           keywords or the admin's pick move afterwards the group can say FERUS PRIME while this
           document actually went to CPTurf. A sent PO is history and must not be relabelled by a
           later config change, so the row says so out loud rather than inheriting the header.
           Silent for every ordinary PO, which is the whole point of moving the name up. */
        /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AND IT WAS NOT SILENT FOR ORDINARY POs.
           Ryan, after sending a routine ECORE order: *"how come after I send the PO to ECORE, on
           the bottom 'Purchase order' row on the PURCHASING card, it says in yellow '— --> ECORE
           ATHLETIC'?"*

           Because the two names are the same supplier spelled two ways, and this compared them
           with `!==`. `poVendor` is stamped `vendor.name` at mint time — the Vendor Guide's
           canonical `ECORE Athletic` — while `_displayVendor` falls through to the name on the
           quote line, `ECORE`. Every ECORE PO ever sent has carried this warning.

           📌 THE ANSWER WAS ALREADY IN THE BUILDING, TWICE OVER. `vendorNamesMatch` in
           dashboard-main.js exists for precisely this pairing and says so in its own comment
           (*"PO records store the canonical Vendor Guide name (e.g. 'ECORE Athletic'), but
           q.financials.vendors holds the Shopify product.vendor short form ('ECORE'). Strict
           equality misses that pairing"*), and po-routes.js's own note at the stamp site remarks
           that the fuzzy matcher *"bridges ECORE / ECORE Athletic"*. This row was the one place
           that asked the question without it.

           ⚠️ THE CHIP STILL EARNS ITS KEEP — Ferus Prime vs CPTurf is a real off-vendor PO and no
           amount of suffix-stripping makes those match. Only the false alarm goes.

           ⚠️ FALLS BACK TO A PREFIX TEST, NOT TO `!==`. dashboard-main.js may not have wired
           RPDash yet when this renders (the same reason every other RPDash call here is guarded).
           Falling back to strict inequality would restore the bug on exactly the paint where the
           helper is missing; the degraded form still catches the case the chip is FOR, because two
           genuinely different suppliers are not prefixes of one another. */
        + ((p.poVendor && !(function (a, b) {
              if (!a || !b) return false;
              if (window.RPDash && typeof window.RPDash.vendorNamesMatch === 'function') {
                return window.RPDash.vendorNamesMatch(a, b);
              }
              var na = String(a).toLowerCase().trim();
              var nb = String(b).toLowerCase().trim();
              return na === nb || na.indexOf(nb + ' ') === 0 || nb.indexOf(na + ' ') === 0;
            })(p.poVendor, _displayVendor))
            ? '<span class="rqd-toggle__date rqd-po-offvendor" title="'
              + escAttr('This PO was issued to ' + p.poVendor + ', not ' + _displayVendor
                + '. A sent PO keeps the supplier it was addressed to.')
              + '">→ ' + esc(p.poVendor) + '</span>'
            : '')
        /* END CUSTOM_PURCHGROUP_BY_CLAUDE */
        + '<span class="rqd-toggle__date">' + esc(p.poNumber) + '</span>'
        + (dateVal ? '<span class="rqd-toggle__date">' + formatDate(dateVal) + '</span>' : '')
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) — the human half of the same fix. The
           data attribute serves the bot; a person reading the card needs to be told too, because
           the green ✓ and the Re-send button look identical either way. Only says anything on a
           PO nobody emailed from here — an ordinary sent PO stays exactly as it was. */
        + ((sent && p.sentExternally)
            ? '<span class="rqd-toggle__date rqd-po-recorded" title="'
              + escAttr('Recorded as sent, not emailed from here. Somebody marked this PO as '
                + 'delivered by phone, portal or another mailbox'
                + (p.sentExternallyBy ? ' (' + p.sentExternallyBy + ')' : '')
                + '. Confirm the supplier has it before chasing the order.')
              + '">recorded, not emailed</span>'
            : '')
        // Void as a slim italic-underlined link inline with the date — matches
        // the freight "cancel" link style. Was a full-size red 140×28 button
        // on the right; the action is rare/destructive so a quieter
        // affordance reads better next to the routine Email button.
        + _driftChip
        + '<button class="rqd-freight-reset-link" data-po-action="void" data-po="' + esc(p.poNumber) + '" type="button" title="Void this PO — locked audit-trail row remains below.">Void</button>'
        // Email is the right-side action (Re-send when already sent), with
        // a "View" button alongside that opens the PDF in a new tab. View
        // only shows once the PO has been sent (before that, admin uses
        // the modal Preview to see the PDF as part of the send flow).
        + '<div class="rqd-invoice">'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE — View PO button (sent POs only).
             Opens the PDF in a new tab so admin can quickly re-check what
             the supplier received without re-opening the email modal. */
          + (sent ? '<button class="rqd-invoice__link rqd-invoice__generate" data-po-action="view-pdf" data-po="' + esc(p.poNumber) + '" type="button" title="Open the sent PO PDF in a new tab">View PO</button>' : '')
          /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
          + (scheduled
              /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) - the pill is a RELEASE control now.
                 It was disabled because the only thing a click could have meant was re-firing
                 Email PO, i.e. a second copy. A click now drains the copy already queued, which
                 is the opposite. Falls back to the old inert pill if the marker is missing or
                 already past - see scheduledReleaseBtnHtml for why a past time offers nothing. */
              /* CUSTOM_POSEND_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE FALLBACK PILL USED TO PRINT A
                 TIME THAT DOES NOT EXIST. An APPROVAL hold has no clock by design, so
                 `scheduledFor` is null, `schedLabel` is '' and this read "Abby sends it at ." —
                 and it pointed at Void + Re-gen, which is not the control: an approval waits for
                 a person on the Agent Approvals page, and a window hold is discarded there too.
                 Three states, three sentences, and every one of them names where the control is. */
              ? (window.RQD.scheduledReleaseBtnHtml({ kind: 'po', ref: safeRef, poNumber: p.poNumber, scheduledFor: p.scheduledFor })
                 || '<button class="rqd-invoice__link rqd-po-scheduled" type="button" disabled title="'
                    + escAttr(awaitingApproval
                        ? (heldBy || 'An agent') + ' composed this PO and it is waiting for an admin to approve it. Nothing has gone to the supplier. Release or discard it on the AI Agents → Approvals page.'
                        : heldBy
                          ? (heldBy + ' queued this PO' + (schedLabel ? '; it sends ' + schedLabel : '')
                             + ' unless it is discarded first. Release or discard it on the AI Agents → Approvals page.')
                          : ('Queued - Abby sends it at ' + schedLabel + '. Void + Re-gen to change it.'))
                    + '">&#9203; ' + (awaitingApproval ? 'Awaiting approval' : 'Scheduled') + '</button>')
              : '<button class="rqd-invoice__link' + (done ? ' rqd-invoice__generate' : '') + '" data-po-action="email" data-po="' + esc(p.poNumber) + '" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button">' + (sent ? 'Re-send PO' : 'Email PO') + '</button>')
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-12) — same "Mark Sent" as the Fulfillment
             card's PO row, so the two PO surfaces stay consistent. Unsent POs only. */
          + ((sent || scheduled) ? '' : '<button class="rqd-invoice__link" data-po-action="mark-sent" data-po="' + esc(p.poNumber) + '" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Record this PO as already sent to the vendor — no email is sent">Mark Sent</button>')
          /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        + '</div>'
        + '</div>';
    } else {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Direct-purchase state.
         When admin used the "Mark Purchased (no PO)" path for this vendor
         (vendor row was bought online via discount code / portal / credit
         card and doesn't need a formal PO), render a done-row similar to
         the PO-sent row but worded for direct purchase. Edit re-opens the
         popover prefilled. Progress tracker's Ordered step is already flipped
         done via lifecycle.ordering[vendor].ordered = true. */
      var vo = (q && q.ordering && q.ordering[vendor]) || {};
      var directPurchased = vo.purchasedDirectly === true;
      if (directPurchased) {
        var purchasedDateStr = vo.purchasedAt ? formatDate(vo.purchasedAt) : '';
        var noteHtml = vo.purchasedNote
          ? '<span class="rqd-toggle__date" title="' + escAttr(vo.purchasedNote) + '">— ' + esc(vo.purchasedNote.length > 32 ? vo.purchasedNote.slice(0, 30) + '…' : vo.purchasedNote) + '</span>'
          : '';
        html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs('purchase-order', 'purchased-direct') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>'
          /* ⛔ NOT "Purchase order" — this row is the record that the goods were bought WITHOUT
             one. Collapsing it into the standard step name would state the opposite of what
             happened, which is exactly the kind of thing a fulfillment bot would act on. */
          /* CUSTOM_WEBORDER_BY_CLAUDE (Ryan 2026-09-06) — a web-order vendor says HOW it was
             bought. RW-1317-C's PowerBlock group is placed on their own portal; "Purchased direct"
             is the phone-order wording and this is the web one. Same row, same step key. */
          + '<span>' + (_isWebOrderVendor ? 'Purchased online' : 'Purchased direct') + '</span>'
          + (purchasedDateStr ? '<span class="rqd-toggle__date">' + esc(purchasedDateStr) + '</span>' : '')
          + noteHtml
          + '<div class="rqd-invoice">'
            + '<button class="rqd-invoice__link rqd-invoice__generate" data-po-action="mark-purchased-edit" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" type="button" title="Edit direct-purchase entry or revert to PO">Edit</button>'
          + '</div>'
          + '</div>';
      } else {
        var poBtnAttrs = poGated
          ? 'disabled title="' + escAttr(poGateReason) + '"'
          : 'type="button" style="cursor:pointer;"';
        var poBtnClass = poGated ? 'rqd-invoice__link rqd-invoice__link--disabled' : 'rqd-invoice__link';
        /* Single dropdown button — both Generate PO + Mark Purchased (no PO)
           live in the menu equally. Default label "-- Select --" (Ryan's
           preference) so neither path is biased as the primary; admin picks
           whichever applies per vendor. Menu open via the existing
           data-po-action="purchase-action-menu" handler. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Web-order vendors
           (flagged in Vendor Guide as workflow='web-order') swap the
           "-- Select --" label for "Mark Web Purchased". The click still
           goes through openPurchaseActionMenu which skips the menu and
           routes straight to the Mark Purchased popover for web-order
           vendors. Reuses _isWebOrderVendor from the freight-handshake
           gate at the top of this function. */
        var _btnLabel = _isWebOrderVendor ? 'Mark Purchased' : '-- Select --';
        var _btnAria = _isWebOrderVendor ? 'Mark web purchase for ' + safeDisplay : 'Purchase action for ' + safeDisplay;
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        html += '<div class="rqd-toggle"' + stepAttrs('purchase-order', 'not-ordered') + '>'
          + '<div class="rqd-toggle__check" style="cursor:default;"></div>'
          /* CUSTOM_WEBORDER_BY_CLAUDE (Ryan 2026-09-06) — there is no PO on a web-order vendor;
             the goods are bought on their site. Ryan on RW-1317-C's PowerBlock group: *"the row's
             text reads 'Purchase order' when technically there is no PO … this should instead
             read 'Purchased online'."* The step key stays `purchase-order` so every reader of
             `data-step` (the progress bar, the Order Desk lens) still finds the same step. */
          + '<span>' + (_isWebOrderVendor ? 'Purchased online' : 'Purchase order') + '</span>'
          + '<div class="rqd-invoice">'
            /* The picker sits INSIDE .rqd-invoice, first — so it inherits that container's
               margin-left:auto and lands right-aligned on the same row, immediately left of the
               purchase button, which is the order the two are used in. */
            + _aliasPickHtml
            + '<button class="' + poBtnClass + ' rqd-po-select-btn" data-po-action="purchase-action-menu" data-vendor="' + safeVendor + '" data-ref="' + safeRef + '" ' + poBtnAttrs + ' aria-haspopup="menu" aria-label="' + _btnAria + '">' + _btnLabel + '</button>'
          + '</div>'
          + '</div>';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    }

    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — prepended, not appended, so the header is
       built LAST and emitted FIRST. `_displayVendor` is only final after the alias picker, the
       stored pick and the keyword answer have all been resolved above, and a header written at the
       top of the function would have captured the row's raw name instead. */

    voided.forEach(function (v) {
      html += '<div class="rqd-toggle rqd-toggle--locked rqd-po-voided-row"' + stepAttrs('purchase-order', 'voided') + '>'
        + '<div class="rqd-toggle__check" style="cursor:default;">&#128274;</div>'
        + '<span>Purchase order</span>'
        + '<span class="rqd-toggle__date">' + esc(v.poNumber) + ' · voided</span>'
        + (v.voidedAt ? '<span class="rqd-toggle__date">' + formatDate(v.voidedAt) + '</span>' : '')
        + '</div>';
    });

    /* CUSTOM_PURCHGROUP_BY_CLAUDE (Ryan 2026-08-27) — the header is built LAST and emitted FIRST.
       `_displayVendor` is only final after the alias picker, the stored pick and the keyword answer
       have all resolved above, so a header written at the top of the function would have captured
       the row's raw name instead.

       ⛔ AND ONLY WHEN THERE ARE ROWS UNDER IT. The caller joins vendor blocks with a separator and
       skips empty ones — unconditionally prepending would make every block truthy, so a vendor that
       renders nothing would contribute a bare heading and an orphaned divider. Returning '' keeps
       that guard working. */
    if (!html) return '';
    return _groupHead + html;
  }

  /* CUSTOM_PAYTERMSGATE_BY_CLAUDE (AIOPS `87238b70d3ee`, Ryan 2026-09-18) — ⛔ THE TERMS AND THE
     BALANCE, WHERE THE BOOK DECISION IS MADE.
     ─────────────────────────────────────────────────────────────────────────────
     Ryan, teaching on SHOP-2084: *"Before GoRocket Book / schedule pickup / send ready-to-ship
     carrier, agents MUST know payment terms AND paid status… Blind schedule on unpaid 50/50 =
     massive issues."* All three facts were on the deal and none of them was on the card an
     operator opens to send the PO or stage the pickup.

     ⛔ IT RENDERS THE SERVER'S VERDICT; IT DOES NOT COMPUTE ONE. `q.paymentGate` comes from
     lib/payment-terms-gate.js through the deal summary, so this card, the Fulfillment card and an
     agent reading the baked record cannot answer "may this ship?" three different ways — the
     drift that the shared `vendorNeedsFreightHandshake` predicate exists to prevent one card over.

     ⛔ IT IS A STATEMENT, NOT A GATE. Nothing here disables Generate PO or hides a control:
     Ryan's card asks for a DD bake that refuses a Book HAND-OFF, and no Book button exists on this
     screen to refuse — the hand-off is an email an agent composes. Turning an unpaid 50/50 into a
     locked card would block the purchase order too, which is a different decision he has not made.
     The hard stop that bites is the one an agent reads off `paymentGate.mayBook`.

     ⚠️ A DEAL WITH NOTHING OWED RENDERS NOTHING. A green "paid in full" row on every settled deal
     is a line people stop seeing, and this row's whole job is to be unusual. */
  function paymentGateRow(q, helpers) {
    var h = helpers || {};
    var esc = h.esc || function (s) { return String(s == null ? '' : s); };
    var gate = q && q.paymentGate;
    if (!gate || gate.shipGate === 'clear') return '';
    /* CUSTOM_PAYTERMSGATE_BY_CLAUDE (Ryan 2026-09-19, SHOP-2022) — ⛔ NOTHING OWED IS NOTHING TO
       SAY. `review` is reachable with a balance of exactly zero when the deal total is unknown:
       an unpriced deal, and — the case that actually shows up — a fully REFUNDED online order,
       whose current total Shopify drives to 0 (SHOP-1924's note at quote-dashboard.js ~L1190).
       Both rendered a "$0.00 owed" row on a card whose whole job is to flag money outstanding.
       `hold` cannot reach zero (the settled branch returns before it), so this only ever drops
       the empty case. */
    if (!(Number(gate.balanceDue) > 0)) return '';
    var isHold = gate.shipGate === 'hold';
    /* `--warn` is the row treatment the freight outlier and the roll-length warning already wear;
       a third private class for "look at this" is a fourth vocabulary on one card. */
    return '<div class="rqd-toggle rqd-payment-gate' + (isHold ? ' rqd-payment-gate--hold' : '')
      + '" data-step="payment-terms" data-state="' + esc(gate.shipGate) + '"'
      + ' title="' + esc(gate.why) + '">'
      + '<div class="rqd-toggle__check" style="cursor:default;">' + (isHold ? '&#9940;' : '&#9888;') + '</div>'
      + '<span>' + esc(gate.label) + (isHold ? ' — balance due before ship' : ' — balance outstanding') + '</span>'
      + '<span class="rqd-toggle__date">'
        + esc('$' + Number(gate.balanceDue || 0).toFixed(2)) + ' owed'
      + '</span>'
      + '</div>';
  }
  /* END CUSTOM_PAYTERMSGATE_BY_CLAUDE */

  window.dashboardPoLens = { attach: attach, renderVendorRow: renderVendorRow, paymentGateRow: paymentGateRow };
})();
